import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { urlSourceLabel, urlSourceFromProbe } from '../utils/urlSource';
import { AddCameraWizard, EditCameraModal } from './AdminConfigPage';
import { apiFetch, apiImageUrl } from '../utils/streamApi';
import { AddKnownPersonForm } from './AddKnownPersonPage';
import CameraStreamSettings from '../components/CameraStreamSettings';
import PasswordInput from '../components/PasswordInput';
import { requestPushPermission, pushSupported } from '../utils/pushNotifications';
import { useEntitlements, productHas, boxHas } from '../utils/entitlements';
import { Field, Choice, Pick, Toggle, Panel, SettingRow } from '../components/setup/SetupBits';
import AttendancePanel from '../components/setup/AttendancePanel';
import PatrolPanel     from '../components/setup/PatrolPanel';
import PacsPanel       from '../components/setup/PacsPanel';
import AccessPanel     from '../components/setup/AccessPanel';
import ZonesPanel      from '../components/setup/ZonesPanel';
import ZoneWatchPanel  from '../components/setup/ZoneWatchPanel';
import CountingPanel   from '../components/setup/CountingPanel';
import ShopAnalyticsPanel from '../components/setup/ShopAnalyticsPanel';
import SeatsPanel      from '../components/setup/SeatsPanel';
import { MODELS, MODEL_SETTINGS, PPE_ITEMS, modelLoad, withDefaults, nonDefault,
         askSettings, tunedSettings, FACE_ON_SET, FACE_OFF_DETECTOR, FACE_AWARE, IDENTITY,
         needsFallbackAlerter }
  from '../utils/aiModels';
import '../css/guided-setup.css';

// ─── Guided Setup ─────────────────────────────────────────────────────────────
//
// The end-user setup wizard. A customer receives a preconfigured box, logs in,
// and finishes setup themselves without an installer visit.
//
// Two rules shape every screen here:
//
//  1. Only ask what is required. Anything the box can discover (stream URLs,
//     MACs, device kinds, codecs) is discovered, not asked. The customer answers
//     security questions — what to watch, who belongs, when, and what happens.
//
//  2. No green tick the system has not verified. Every "connected" / "paired" /
//     "working" claim on these screens is backed by a live round trip, because a
//     customer who believes a dead setup is live is worse off than one who knows
//     it is broken.
//
// Steps whose `key` is set report progress to /ui/setup/state, so a closed tab
// or a browser crash resumes where the customer left off.

// THE HOME FLOW IS DELIBERATELY SHORT — keep in step with _SETUP_STEPS in
// parvekshak_api.py, which explains what was dropped and why. This customer
// bought a box online and is installing it themselves; ten screens is where they
// abandon setup or click through without reading.
//
// Only what the product cannot work without, plus ONE screen carrying the two
// questions that earn their place: "at night, should anyone be here?" and "does
// this see the road or a neighbour's place?". Everything else stays reachable
// from the dashboard afterwards.
const STEPS = [
  { id: 'welcome'               , rail: 'Welcome'       },
  { id: 'cameras'  , key: 'cameras'  , rail: 'Cameras'  },
  { id: 'phone'    , key: 'phone'    , rail: 'Phone alerts' },
  { id: 'devices'  , key: 'devices'  , rail: 'Siren & lights' },
  { id: 'perimeter', key: 'perimeter', rail: 'Your boundary' },
  { id: 'ready'                 , rail: 'Done'          },
];

// ─── The industrial list ──────────────────────────────────────────────────────
//
// An industrial site is set up by an installer or a plant's own admin, and it
// answers a different set of questions. Two steps arrive:
//
//   models    which AI models run on which cameras, and their site-wide settings.
//             A plant wants PPE on the shop floor and not on the car park, so
//             this is the matrix the home wizard never needed.
//   modules   attendance, guard patrol and access correlation — switched on and
//             pointed at the cameras and zones they need to work at all.
//
// "animals" goes: a plant with stray dogs sets that per camera on the AI Models
// page, and asking a factory about pets reads as the wrong product.
//
// "linger" STAYS, and matters more here than at a home. People stand and work in
// view for minutes on a shop floor, which is exactly the case osc_from_origin
// fixes — see the note above LINGER_ON.
//
// Keys must stay in step with _SETUP_STEPS_INDUSTRIAL in parvekshak_api.py: the
// box computes `outstanding` from its own list, and a key here that the box does
// not know is rejected by PUT /ui/setup/state.
const INDUSTRIAL_STEPS = [
  { id: 'welcome'            , rail: 'Welcome'           },
  { id: 'property', key: 'property', rail: 'Site'        },
  { id: 'cameras' , key: 'cameras' , rail: 'Cameras'     },
  { id: 'areas'   , key: 'areas'   , rail: 'Ignore areas' },
  { id: 'linger'  , key: 'linger'  , rail: 'How people move', skippable: true },
  { id: 'models'  , key: 'models'  , rail: 'AI models'   },
  { id: 'modules' , key: 'modules' , rail: 'Site modules', skippable: true },
  { id: 'people'  , key: 'people'  , rail: 'Staff'       , skippable: true },
  { id: 'phone'   , key: 'phone'   , rail: 'Phone alerts' },
  { id: 'schedule', key: 'schedule', rail: 'Hours'       },
  { id: 'devices' , key: 'devices' , rail: 'Alert devices', skippable: true },
  { id: 'test'    , key: 'test'    , rail: 'Live test'   , skippable: true },
  { id: 'ready'               , rail: 'Done'             },
];

// Field / Choice / Toggle / Panel / SettingRow live in components/setup/SetupBits
// so the industrial module panels are built from the same parts — see the import
// above. They were defined here until those panels needed them.

// A camera snapshot. Resolves the token-bearing URL, and shows why it is blank
// rather than an unexplained black rectangle.
// /cameras/{idx}/frame, not /snapshot: an <img> can't send the Bearer header,
// so the token rides in ?token= — which /frame accepts and /snapshot does not
// (that is why every camera showed "No picture yet"). /frame also reads the
// already-open stream, so it is the faster of the two. refreshMs > 0 re-pulls
// the frame so the customer sees what each camera is looking at right now.
function Snapshot({ idx, bust, refreshMs = 0 }) {
  const [src, setSrc]   = useState(null);
  const [bad, setBad]   = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!refreshMs) return undefined;
    const t = setInterval(() => setTick(n => n + 1), refreshMs);
    return () => clearInterval(t);
  }, [refreshMs]);
  useEffect(() => {
    let alive = true;
    const t = `${bust || 0}-${tick}`;
    apiImageUrl(`/cameras/${idx}/frame?t=${t}`)
      .then(u => {
        if (!alive) return;
        // Preload, so a refresh swaps frames without flashing back to "Loading…".
        const img = new Image();
        img.onload  = () => { if (alive) { setSrc(u); setBad(false); } };
        img.onerror = () => { if (alive && !src) setBad(true); };
        img.src = u;
      })
      .catch(() => { if (alive) setBad(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, bust, tick]);
  if (bad || !src) {
    return <div className="gs-cam-shot-empty">{bad ? 'No picture yet' : 'Loading…'}</div>;
  }
  return <img className="gs-cam-shot" src={src} alt="" />;
}

// ─── 1. Welcome ───────────────────────────────────────────────────────────────

function WelcomeStep({ setNav }) {
  useEffect(() => { setNav({ nextLabel: 'Start setup' }); }, [setNav]);
  return (
    <>
      <h1 className="gs-h1">Welcome to Parvekshak</h1>
      <p className="gs-lede">
        Let’s set up your security system. It takes about ten minutes, and you can
        stop at any point — we remember where you were.
      </p>
      <Panel title="What we’ll do">
        Find your cameras, mark the areas you want watched,
        {productHas('known-faces-add') ? ' add the people who live here,' : ''} send a test
        alert to your phone, and then check it all works by having someone walk past a camera.
      </Panel>
      <p className="gs-help">
        You won’t need to know anything technical. If something can be worked out
        automatically, we work it out.
      </p>
    </>
  );
}

// ─── 2. Property ──────────────────────────────────────────────────────────────

// The kinds offered. An industrial site is not "a house or apartment", and the
// answer rides on every alert, so the list is the one the customer recognises.
const PROPERTY_KINDS = [
  { kind: 'home',   icon: '🏠', title: 'Home',   sub: 'A house or apartment' },
  { kind: 'office', icon: '🏢', title: 'Office', sub: 'A shop, office or godown' },
  { kind: 'other',  icon: '📍', title: 'Other',  sub: 'A farm, plot or site' },
];
const SITE_KINDS = [
  { kind: 'factory',   icon: '🏭', title: 'Factory',   sub: 'Production floor, shed or plant' },
  { kind: 'warehouse', icon: '📦', title: 'Warehouse', sub: 'Godown, cold store or yard' },
  { kind: 'office',    icon: '🏢', title: 'Office',    sub: 'Corporate or back office' },
  { kind: 'other',     icon: '📍', title: 'Other site',sub: 'Site, campus or mixed use' },
];

function PropertyStep({ setNav, onDone, industrial }) {
  const [kind,   setKind]   = useState(industrial ? 'factory' : 'home');
  const [name,   setName]   = useState('');
  const [owner,  setOwner]  = useState('');
  const [mobile, setMobile] = useState('');
  const [err,    setErr]    = useState(null);

  useEffect(() => {
    apiFetch('/ui/setup/property')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d) return;
        setKind(d.kind || (industrial ? 'factory' : 'home'));
        setName(d.name || '');
        setOwner(d.owner || '');
        setMobile(d.mobile || '');
      })
      .catch(() => {});
  }, [industrial]);

  const save = useCallback(async () => {
    setErr(null);
    const res = await apiFetch('/ui/setup/property', {
      method: 'PUT',
      json: { kind, name: name.trim(), owner: owner.trim(), mobile: mobile.trim() },
    });
    if (!res.ok) { setErr('Could not save. Check your connection and try again.'); return false; }
    await onDone({ kind });
    return true;
  }, [kind, name, owner, mobile, onDone]);

  useEffect(() => {
    setNav({ canNext: !!name.trim(), onNext: save,
             note: name.trim() ? null : 'Give your property a name to continue' });
  }, [setNav, name, save]);

  return (
    <>
      <h1 className="gs-h1">{industrial ? 'What are you watching?' : 'What are you protecting?'}</h1>
      <p className="gs-lede">
        {industrial
          ? 'This names the site and tells us who to contact. It appears on every alert, '
            + 'so a supervisor can tell at a glance which plant it came from.'
          : 'This names your system and tells us who to contact. It appears on your '
            + 'alerts so you can tell at a glance which property they came from.'}
      </p>

      <Field label={industrial ? 'Type of site' : 'Type of property'}>
        <div className="gs-choices">
          {(industrial ? SITE_KINDS : PROPERTY_KINDS).map(k => (
            <Choice key={k.kind} icon={k.icon} title={k.title} sub={k.sub}
                    selected={kind === k.kind} onClick={() => setKind(k.kind)} />
          ))}
        </div>
      </Field>

      <Field
        label={industrial ? 'Site name' : 'Property name'}
        help={industrial
          ? 'Whatever this site is called on site. It is shown on every alert — “Bay 2 at Unit 3” reads better than a serial number.'
          : 'Whatever you call this place. It is shown on every alert — “Front gate at Green Villa” reads better than a serial number.'}
      >
        <input className="gs-input" value={name} onChange={e => setName(e.target.value)}
               placeholder={industrial ? 'e.g. Unit 3, Bhiwandi' : 'e.g. Green Villa'} maxLength={60} />
      </Field>

      <div className="gs-section-title">Contact (optional)</div>

      <Field label={industrial ? 'Site contact' : 'Your name'}
             help={industrial
               ? 'Who a responder should ask for on arrival.'
               : 'Used when the system needs to say who to reach.'}>
        <input className="gs-input" value={owner} onChange={e => setOwner(e.target.value)}
               placeholder="Full name" maxLength={60} />
      </Field>

      <Field
        label="Mobile number"
        help="For your installer or a responder to call if something needs attention. It is kept on this box and shown on alerts forwarded to the people you add."
      >
        <input className="gs-input" value={mobile} onChange={e => setMobile(e.target.value)}
               placeholder="e.g. 98765 43210" maxLength={20} inputMode="tel" />
      </Field>

      {err && <div className="gs-err">{err}</div>}
    </>
  );
}

// ─── 3. Cameras ───────────────────────────────────────────────────────────────

// "Cameras on another network?" (PRVSK-156) — what the installer typed, as the
// /24 the scanner sweeps: "192.168.1", "192.168.1.x", "192.168.1.0/24" or any
// address in it → { cidr: "192.168.1.0/24", label: "192.168.1.x" }. Private
// ranges only — the scanner ignores anything else and would fall back to a
// full default sweep.
function toOtherNet(raw) {
  const t = String(raw || '').trim().replace(/\/\d+$/, '').replace(/\.(x|\*)?$/i, '');
  const p = t.split('.');
  if (p.length < 3 || p.length > 4 || !p.slice(0, 3).every(n => /^\d{1,3}$/.test(n) && +n <= 255)) return null;
  const [a, b, c] = p.map(Number);
  const priv = a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
  return priv ? { cidr: `${a}.${b}.${c}.0/24`, label: `${a}.${b}.${c}.x` } : null;
}

function CamerasStep({ setNav, onDone, cameras, reloadCameras }) {
  const [scanning, setScanning] = useState(false);
  const [found,    setFound]    = useState(null);   // discovered, not yet added
  const [deepNote, setDeepNote] = useState(null);   // result line of "Search deeper" / another network
  // Another network (PRVSK-156): a second router's subnet the box can reach but
  // doesn't sweep by default. upstream = what the scan saw behind another router.
  const [otherNet, setOtherNet] = useState('');
  const [netMiss,  setNetMiss]  = useState(null);   // label of a network where nothing answered
  const [netBad,   setNetBad]   = useState(false);  // what was typed isn't a network
  const [upstream, setUpstream] = useState([]);
  const [username, setUsername] = useState('admin');   // most cameras' default; editable
  const [password, setPassword] = useState('');
  const [adding,   setAdding]   = useState(false);
  const [err,      setErr]      = useState(null);
  const [names,    setNames]    = useState({});     // idx → edited name
  const [note,     setNote]     = useState(null);
  const [camTypes, setCamTypes] = useState({});     // key → { label } from /ui/cameras/types
  const [camType,  setCamType]  = useState('auto'); // optional brand; 'auto' tries every type
  const [failed,   setFailed]   = useState([]);     // cameras that refused the shared password
  const [wizardOpen, setWizardOpen] = useState(false);
  // Found cameras the installer unticked (a neighbour's, an old one) — not connected.
  const [skip,     setSkip]     = useState(() => new Set());
  // Connect progress per camera (PRVSK-128): ip → { state: waiting | running |
  // done | failed, n, label, secs }. A 2-minute spinner told nobody anything.
  const [prog,     setProg]     = useState({});
  const [runStart, setRunStart] = useState(null);
  const [, setTick] = useState(0);
  const setP = (ip, patch) => setProg(p => ({ ...p, [ip]: { ...(p[ip] || {}), ...patch } }));
  useEffect(() => {
    if (!runStart) return undefined;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [runStart]);  // Settings' Add Camera wizard, for one the scan missed
  const [editCam,  setEditCam]  = useState(null);   // camera open in Settings' Edit Camera
  const [editTab,  setEditTab]  = useState('basic'); // which tab it opens on ('gate' from the doorbell row)
  // Doorbell (PRVSK-139), set right on the camera card: off / names / instant.
  const bellOf = c => {
    const v = c.alert_actions?.doorbell;
    return v === 'instant' ? 'instant' : (v === true || v === 'names') ? 'names' : 'off';
  };
  const hasGateArea = c => (c.cam_config?.doorbell_zones || [])
    .some(z => (z?.polygon?.length || 0) >= 3 || z?.x1 != null);
  const [bellErr, setBellErr] = useState(null);
  const setCamBell = async (c, mode) => {
    setBellErr(null);
    try {
      const r = await apiFetch(`/cameras/${c.idx}`, {
        method: 'PUT',
        json: { alert_actions: { ...(c.alert_actions || {}), doorbell: mode === 'off' ? false : mode } },
      });
      if (!r.ok) throw new Error();
    } catch {
      setBellErr(`Couldn’t change the doorbell on ${c.name} — try again.`);
    }
    reloadCameras();
  };
  const [removing, setRemoving] = useState(null);   // idx of the camera asking "Remove?"
  // Stream optimise is never done unasked: each card asks; these drive the
  // "Optimise all" button and the reminder on Continue.
  // idx → { needs, refused }: needs = not on recommended and nobody answered;
  // refused = an optimise was sent and the camera didn't keep it.
  const [streamNeeds, setStreamNeeds] = useState({});
  const [streamKey,   setStreamKey]   = useState(0);   // bump → every card re-reads its camera
  const [optAll,      setOptAll]      = useState(null); // { done, total } while optimising all
  const [reminded,    setReminded]    = useState(false);
  const onStreamState = useCallback((idx, st) =>
    setStreamNeeds(m => (m[idx]?.needs === st.needs && m[idx]?.refused === !!st.refused ? m
      : { ...m, [idx]: { needs: st.needs, refused: !!st.refused } })), []);
  // To optimise: not tried yet. Refused ones are only reported — sending the same
  // settings again would fail the same way.
  const needIdx    = cameras.map(c => c.idx).filter(i => streamNeeds[i]?.needs && !streamNeeds[i]?.refused);
  const refusedIdx = cameras.map(c => c.idx).filter(i => streamNeeds[i]?.needs && streamNeeds[i]?.refused);
  const [removeBusy, setRemoveBusy] = useState(false);

  // Brand is optional. Knowing it means the probe tries only that brand's
  // stream URLs — faster, and fewer failed logins, which some cameras (CP Plus)
  // answer by locking the account for a while.
  useEffect(() => {
    apiFetch('/ui/cameras/types')
      .then(r => (r.ok ? r.json() : {}))
      .then(d => setCamTypes(d || {}))
      .catch(() => {});
  }, []);
  const scanSeq      = useRef(0);       // id of the newest scan; older ones must not touch state
  const seenIps      = useRef([]);      // every address the last search showed — "Search deeper" skips them
  const autoScanned  = useRef(false);   // React StrictMode runs mount effects twice in dev

  // Scan automatically on arrival when no camera is configured yet. A customer
  // who already has cameras set up by the installer should not be made to wait
  // for a network sweep they do not need.
  useEffect(() => {
    if (autoScanned.current) return;
    autoScanned.current = true;
    if (cameras.length === 0 && found === null && !scanning) scan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // deep = "Search deeper": asks each device on the network directly, skips the
  // ones already shown, and adds what it finds to the list instead of replacing it.
  // net = another network ({cidr, label}, PRVSK-156): the scanner's gentle sweep of
  // that /24, added to the list the same way and tagged with where it came from.
  async function scan(deep = false, net = null) {
    // Only the newest scan may update the screen. An older one that was
    // superseded (Search again, or a double mount) must exit silently — before,
    // its `finally` hid the spinner while the newer scan was still running, and
    // the step showed neither results nor "no cameras found".
    const seq = ++scanSeq.current;
    const live = () => scanSeq.current === seq;
    const merge = deep || !!net;
    const before = merge ? (found || []) : [];
    setScanning(net ? 'net' : deep ? 'deep' : true); setErr(null); setDeepNote(null);
    setNetMiss(null); setNetBad(false);
    if (!merge) { setFound(null); setFailed([]); setSkip(new Set()); }
    // The scanner already resolves "is this one of ours?" by IP *and* MAC,
    // which an IP-only check here would get wrong after a DHCP change.
    // Sorted by address so a site's cameras read in order (.11, .12, … .110).
    const ipKey = ip => (ip || '').split('.').map(n => n.padStart(3, '0')).join('.');
    const show = cams => {
      const known = new Set(before.map(c => c.ip));
      const fresh = cams.filter(c => !c.already_added && !known.has(c.ip))
        .map(c => (net ? { ...c, otherNet: net.label } : c));
      setFound([...before, ...fresh].sort((a, b) => ipKey(a.ip).localeCompare(ipKey(b.ip))));
      return fresh.length;
    };
    try {
      const r = await apiFetch('/ui/cameras/network-scan', {
        method: 'POST',
        json: net ? { subnets: [net.cidr] } : deep ? { deep: true, exclude: seenIps.current } : {} });
      if (!r.ok) throw new Error();
      const { job_id } = await r.json();
      // Cameras show as they are found (the first within seconds); the job is
      // done once the whole sweep and every password check have finished.
      for (let i = 0; i < 200; i++) {
        await new Promise(res => setTimeout(res, 1500));
        if (!live()) return;                            // step left, or rescanned
        const s = await apiFetch(`/ui/cameras/network-scan/${job_id}`);
        if (!s.ok) continue;
        const job = await s.json();
        if (!live()) return;
        if (job.status === 'error') throw new Error(job.error || 'scan failed');
        const added = show(job.cameras || []);
        if (job.status === 'running') continue;
        const ips = (job.cameras || []).flatMap(c => [c.ip, ...(c.also_ips || [])]);
        seenIps.current = merge ? [...new Set([...seenIps.current, ...ips])] : ips;
        if (!merge) {
          // The networks the box saw behind another router — offered below, pre-filled.
          // 192.168.x only: the scan takes every private hop on the way to the
          // internet, and ISPs number their own hops 10.x / 172.16-31.x — a plain
          // one-router home would be told it has a second router. Home routers use
          // 192.168.x; anything else can still be typed in by hand.
          const up = (job.diag?.upstream || []).filter(u => /^192\.168\./.test(u));
          setUpstream(up);
          const first = up.map(toOtherNet).find(Boolean);
          if (first) setOtherNet(v => v || first.label.replace(/\.x$/, ''));
        }
        if (net) {
          if (added) setDeepNote(`Found ${added} more camera${added > 1 ? 's' : ''} on ${net.label}.`);
          else if ((job.cameras || []).length) setDeepNote(`Every camera that answered on ${net.label} is already in your list.`);
          else setNetMiss(net.label);
        } else if (deep) setDeepNote(added
          ? `Found ${added} more camera${added > 1 ? 's' : ''}.`
          : 'No other cameras answered. If one is still missing, add it by its address with + Add Camera.');
        // Re-read the box's own cameras too. The list is otherwise loaded once, when
        // the page opens — if the box was still starting then, it stayed empty and
        // an added camera (found here as already_added) read as "No cameras found".
        await reloadCameras();
        return;
      }
      if (live()) setErr('The search took too long. Check the network and search again.');
    } catch {
      if (live()) setErr('The search could not run. Check that the box is on the same network as your cameras.');
    } finally {
      if (live()) setScanning(false);
    }
  }

  // Resolve a working stream for one camera with the given password and brand,
  // then add it. → { ok: true } | { ok: false, saveErr? }. saveErr = the login
  // worked but the box didn't save the camera (PRVSK-108 #6) — that used to be
  // reported as connected, and the camera silently never appeared.
  async function connectOne(cam, usr, pwd, type, name) {
    const user = (usr || '').trim() || 'admin';
    try {
      // cam_type 'auto' sweeps every URL template and credential pair, which is
      // the whole point of asking the customer for one password instead of
      // asking them what brand each camera is.
      const p = await apiFetch('/ui/cameras/probe', {
        method: 'POST',
        json: { ip: cam.ip, cam_type: type, user, password: pwd, auth_required: true,
                ...(cam.onvif_xaddr ? { onvif_xaddr: cam.onvif_xaddr } : {}) },
      });
      if (!p.ok) { setP(cam.ip, { state: 'failed' }); return { ok: false }; }
      const { job_id } = await p.json();
      setP(cam.ip, { state: 'running', n: 0, label: 'Starting…', secs: 0 });
      let result = null, why = {};
      // 'auto' sweeps every brand's templates (each can take ~6 s), so it gets
      // longer before we give up than a single known brand does.
      const polls = type === 'auto' ? 120 : 60;
      for (let i = 0; i < polls; i++) {
        await new Promise(res => setTimeout(res, 1500));
        const s = await apiFetch(`/ui/cameras/probe/${job_id}`);
        if (!s.ok) continue;
        const job = await s.json();
        if (job.step) setP(cam.ip, { n: job.step.n, label: job.step.label, secs: job.elapsed_s });
        if (job.status === 'done') { result = job.result; break; }
        if (job.status === 'failed' || job.status === 'error') {
          why = { kind: job.error_kind || null, attemptsLeft: job.attempts_left ?? null };
          break;
        }
      }
      if (!result?.rtsp_url) { setP(cam.ip, { state: 'failed' }); return { ok: false, ...why }; }
      setP(cam.ip, { label: 'Saving…' });
      let saved;
      try {
        saved = await apiFetch('/ui/cameras', {
          method: 'POST',
          json: {
            name: cam.name || name,
            ip: cam.ip,
            mac: cam.mac || '',
            rtsp_url: result.rtsp_url,
            ...(result.rtsp_url_sub ? { rtsp_url_sub: result.rtsp_url_sub } : {}),
            url_source: urlSourceFromProbe(result.method),
            user: result.user || user,
            password: result.password || pwd,
            ...(type !== 'auto' ? { type } : {}),
            enabled: true, nvr: false,   // recording starts off; owner turns it on later
          },
        });
      } catch { saved = null; }
      if (saved?.ok) { setP(cam.ip, { state: 'done' }); return { ok: true }; }
      // A timeout (e.g. the ONVIF MAC lookup through the router) can still have
      // saved it — believe the box's camera list, not the failed request.
      const r = await apiFetch('/ui/cameras').catch(() => null);
      const list = r?.ok ? ((await r.json()).cameras || []) : [];
      if (list.some(c => c.ip === cam.ip)) { setP(cam.ip, { state: 'done' }); return { ok: true }; }
      setP(cam.ip, { state: 'failed' });
      const d = saved ? await saved.json().catch(() => null) : null;
      return { ok: false, saveErr: typeof d?.detail === 'string' ? d.detail
        : saved ? `error ${saved.status}` : 'the box didn’t respond' };
    } catch {
      setP(cam.ip, { state: 'failed' });
      return { ok: false };
    }
  }

  // A camera with no password on it (the scan played its stream without a login)
  // is added with the URLs its ONVIF gave — no login stored, nothing to type.
  const isOpen = cam => cam.auth === 'open' && !!cam.open_url;
  // What "Connect" will actually try: ticked cameras that are open, or all
  // ticked ones once a password is typed (PRVSK-132: it counted every camera found).
  const connectable = (found || []).filter(c => !skip.has(c.ip) && (isOpen(c) || password)).length;
  async function addOpen(cam, name) {
    try {
      const r = await apiFetch('/ui/cameras', {
        method: 'POST',
        json: {
          name: cam.name || name, ip: cam.ip, mac: cam.mac || '',
          rtsp_url: cam.open_url,
          ...(cam.open_url_sub ? { rtsp_url_sub: cam.open_url_sub } : {}),
          user: '', password: '', url_source: cam.open_source || 'open', enabled: true, nvr: false,
        },
      });
      if (r.ok) { setP(cam.ip, { state: 'done' }); return { ok: true }; }
      setP(cam.ip, { state: 'failed' });
      const d = await r.json().catch(() => null);
      return { ok: false, saveErr: typeof d?.detail === 'string' ? d.detail : `error ${r.status}` };
    } catch {
      return { ok: false, saveErr: 'the box didn’t respond' };
    }
  }

  // Add every discovered camera with the one password the customer gave. Most
  // sites use one password for all cameras; the ones that refuse it are listed
  // by IP with their own password + brand box (`failed`), instead of the
  // customer having to search again to get them back.
  async function addAll() {
    if (!found || !found.length) return;
    setAdding(true); setErr(null);
    const picked = found.filter(c => !skip.has(c.ip));
    setProg(Object.fromEntries(picked.map(c => [c.ip, { state: 'waiting' }])));
    setRunStart(Date.now());
    const left = [];
    let camNo = cameras.length + 1;
    for (const cam of picked) {
      if (!isOpen(cam) && !password) {
        // No password typed: the open cameras still go in, and these wait with
        // their own login box below instead of blocking the rest.
        setP(cam.ip, { state: 'failed' });
        left.push({ ...cam, user: username, password: '', camType: 'auto', busy: false, bad: false, noPw: true });
        continue;
      }
      const res = isOpen(cam)
        ? await addOpen(cam, `Camera ${camNo}`)
        : await connectOne(cam, username, password, camType, `Camera ${camNo}`);
      if (res.ok) camNo++;
      // The password worked when only the save failed — keep it, so retry is one tap.
      else if (res.saveErr) left.push({ ...cam, user: username, password, camType, busy: false, bad: false, saveErr: res.saveErr });
      else left.push({ ...cam, user: username, password: '', camType: 'auto', busy: false,
                       bad: true, kind: res.kind || null, attemptsLeft: res.attemptsLeft ?? null });
    }
    await reloadCameras();
    setFound(null);
    setFailed(left);
    setAdding(false);
    setRunStart(null);
  }

  const patchFailed = (ip, patch) =>
    setFailed(list => list.map(c => (c.ip === ip ? { ...c, ...patch } : c)));

  async function retryOne(cam) {
    if (!cam.password) return;
    patchFailed(cam.ip, { busy: true, bad: false, saveErr: null, kind: null, attemptsLeft: null });
    const res = await connectOne(cam, cam.user, cam.password, cam.camType, `Camera ${cameras.length + 1}`);
    if (res.ok) {
      await reloadCameras();
      setFailed(list => list.filter(c => c.ip !== cam.ip));
    } else {
      patchFailed(cam.ip, { busy: false, bad: !res.saveErr, saveErr: res.saveErr || null,
                            kind: res.kind || null, attemptsLeft: res.attemptsLeft ?? null });
    }
  }

  // A wrong camera (an old one still on the network, a neighbour's) is removed
  // right here instead of sending the installer to Settings → Cameras.
  async function removeCam(c) {
    setRemoveBusy(true); setErr(null);
    try {
      const r = await apiFetch(`/cameras/${c.idx}`, { method: 'DELETE' });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        setErr(d?.detail || `Could not remove ${c.name}.`);
      }
    } catch {
      setErr(`Could not remove ${c.name} — the box didn’t respond.`);
    }
    // Indices shift after a delete, so drop any half-typed names with them, and
    // make every stream card read its (possibly different) camera again.
    setNames({});
    setStreamNeeds({});
    setStreamKey(k => k + 1);
    setRemoving(null);
    setRemoveBusy(false);
    await reloadCameras();
  }

  async function rename(idx, value) {
    setNames(n => ({ ...n, [idx]: value }));
  }

  async function commitName(idx) {
    const v = (names[idx] || '').trim();
    if (!v) return;
    setErr(null);
    const r = await apiFetch(`/cameras/${idx}`, { method: 'PUT', json: { name: v } });
    if (!r.ok) {
      // e.g. 409: another camera already has this name (PRVSK-103) — say so and
      // put the saved name back instead of leaving an unsaved one in the box.
      const d = await r.json().catch(() => null);
      setErr(d?.detail || 'Could not rename the camera.');
      setNames(n => { const { [idx]: _drop, ...rest } = n; return rest; });
    }
    await reloadCameras();
  }

  // Camera clocks drift, and a wrong clock means every alert carries the wrong
  // time. Fixing it is a silent background action — surfaced only when it fails.
  // only_if_needed (PRVSK-108 #2): a camera on NTP is never switched to a manual
  // clock, and a clock that is already right is left alone.
  async function syncClocks() {
    setNote('Checking camera clocks…');
    let fixed = 0;
    for (const c of cameras) {
      try {
        const r = await apiFetch(`/cameras/${c.idx}/datetime/sync?only_if_needed=true`, { method: 'POST' });
        const d = r.ok ? await r.json() : null;
        if (d?.action === 'corrected') fixed++;
      } catch { /* a camera that cannot sync is not worth blocking setup */ }
    }
    setNote(fixed ? `Camera clocks corrected from this box (${fixed} of ${cameras.length}).` : null);
  }

  async function optimiseAll() {
    const list = [...needIdx];
    setOptAll({ done: 0, total: list.length }); setErr(null);
    let failedN = 0, notKeptN = 0;
    for (let n = 0; n < list.length; n++) {
      try {
        const r = await apiFetch(`/cameras/${list[n]}/onvif/streams`, { method: 'PUT', json: { preset: 'recommended' } });
        const d = r.ok ? await r.json() : null;
        if (!d?.ok) failedN++;
        // Answered, but the camera kept its old values (PRVSK-132 read-back).
        else if (Object.values(d.applied?.kept || {}).some(v => v === false)) notKeptN++;
      } catch { failedN++; }
      setOptAll({ done: n + 1, total: list.length });
    }
    setOptAll(null);
    setStreamKey(k => k + 1);            // cards show what each camera now reports
    const bad = failedN + notKeptN;
    if (bad) setErr(`${bad} of ${list.length} camera${list.length > 1 ? 's' : ''} didn’t take the recommended settings — see ${bad > 1 ? 'each' : 'the'} camera card.`);
  }

  const proceed = useCallback(async () => {
    // Remind once, never block: cameras left on their own heavy settings make
    // the box slower and use more disk.
    if (needIdx.length && !reminded) {
      setReminded(true);
      return false;
    }
    await syncClocks();
    await onDone({ count: cameras.length });
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameras.length, onDone, needIdx.length, reminded]);

  useEffect(() => {
    setNav({
      canNext: cameras.length > 0 && !scanning && !adding && !optAll && !failed.some(c => c.busy),
      onNext: proceed,
      note: cameras.length === 0 ? 'At least one camera is needed before you can continue' : null,
    });
  }, [setNav, cameras.length, scanning, adding, optAll, failed, proceed]);

  return (
    <>
      <h1 className="gs-h1">Finding your cameras</h1>
      <p className="gs-lede">
        We look for cameras on the same network as this box. You do not need to know
        their addresses — we find them and work out how to connect.
      </p>

      {scanning && (
        <div className="gs-panel">
          <div className="gs-scanning"><span className="gs-spin" />{' '}
            {scanning === 'deep' ? 'Asking every device on your network directly…'
              : scanning === 'net' ? `Searching ${toOtherNet(otherNet)?.label || 'the other network'} — asking each address directly…`
              : 'Searching your network…'}
            {found && found.length > 0
              ? ` ${found.length} found so far — cameras appear here as they answer.`
              : ' Cameras appear here as they answer; this can take a minute.'}
          </div>
        </div>
      )}

      {found !== null && found.length > 0 && (
        <>
          <Panel tone="good" title={`${found.length} new camera${found.length > 1 ? 's' : ''} found`}>
            {adding && runStart && (() => {
              const doneN = found.filter(c => prog[c.ip]?.state === 'done').length;
              const settled = found.filter(c => ['done', 'failed'].includes(prog[c.ip]?.state)).length;
              const sec = Math.round((Date.now() - runStart) / 1000);
              return (
                <div className="gs-run" role="status">
                  <div className="gs-run-head">
                    <span>{doneN} of {found.length} connected</span>
                    <span className="gs-run-time">{Math.floor(sec / 60)}:{String(sec % 60).padStart(2, '0')} so far</span>
                  </div>
                  <div className="gs-run-bar"><div style={{ width: `${Math.round(100 * settled / found.length)}%` }} /></div>
                </div>
              );
            })()}
            <div className="gs-found">
              {found.map(c => {
                const open  = isOpen(c);
                const label = [c.maker, c.model].filter(Boolean).join(' ') || c.vendor || '';
                return (
                  <div className={`gs-found-row${skip.has(c.ip) ? ' skipped' : ''}`} key={c.ip}>
                    <input type="checkbox" className="gs-found-pick" checked={!skip.has(c.ip)} disabled={adding}
                           aria-label={`Connect ${c.ip}`}
                           onChange={e => setSkip(sk => {
                             const n = new Set(sk);
                             if (e.target.checked) n.delete(c.ip); else n.add(c.ip);
                             return n;
                           })} />
                    <span className="gs-found-lock" aria-hidden="true">{open ? '🔓' : c.auth ? '🔒' : '…'}</span>
                    <span className="gs-found-ip">{c.name || c.ip}</span>
                    {label && <span className="gs-found-model">{label}</span>}
                    {c.otherNet && <span className="gs-net-chip">other network · {c.otherNet}</span>}
                    {c.also_ips?.length > 0 && (
                      <span className="gs-found-model" title="The same camera answers on more than one address">
                        also on {c.also_ips.map(a => '.' + a.split('.').pop()).join(', ')}
                      </span>
                    )}
                    {adding && prog[c.ip] ? (
                      <span className={`gs-found-prog ${prog[c.ip].state}`}>
                        {prog[c.ip].state === 'waiting' && 'Waiting for its turn'}
                        {prog[c.ip].state === 'running' && (
                          <><span className="gs-spin" /> {prog[c.ip].label}
                            {prog[c.ip].n ? ` · step ${prog[c.ip].n} of 4` : ''}
                            {prog[c.ip].secs != null ? ` · ${Math.round(prog[c.ip].secs)} s` : ''}</>
                        )}
                        {prog[c.ip].state === 'done' && '✓ Connected'}
                        {prog[c.ip].state === 'failed' && '✗ Not connected'}
                      </span>
                    ) : (
                      <span className={`gs-found-auth${open ? ' open' : ''}`}>
                        {open ? 'No password needed'
                          : !c.auth ? 'Checking…'
                          : c.auth === 'required' ? 'Needs password' : 'May need a password'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {found.some(isOpen) && (
              <p className="gs-help gs-found-note">
                🔓 These cameras have no password, so anyone on your Wi-Fi can view them.
                You can set one in the camera’s own app.
              </p>
            )}
          </Panel>
          {found.some(c => !isOpen(c)) && (
          <Field
            label={found.some(isOpen)
              ? `Camera login (for the ${found.filter(c => !isOpen(c)).length} camera${found.filter(c => !isOpen(c)).length > 1 ? 's' : ''} marked 🔒)`
              : 'Camera login'}
            help="The username and password your cameras were set up with — ask whoever installed them, or check the label on the camera. Most cameras use “admin”. We try it on all of them, so you only type it once."
            helpAfter={found.some(isOpen)
              ? 'Don’t have it? Connect anyway: the 🔓 cameras are added now, and these wait for their own password.'
              : 'Don’t have it? Continue without adding these, and your installer can add them later.'}
          >
            <div className="gs-cred-row">
              <input className="gs-input gs-cred-user" value={username}
                     onChange={e => setUsername(e.target.value)}
                     placeholder="Username" aria-label="Camera username"
                     autoComplete="off" autoCapitalize="none" spellCheck={false} />
              <PasswordInput className="gs-input" wrapClassName="gs-cred-pass" value={password}
                     onChange={e => setPassword(e.target.value)}
                     placeholder="Camera password" aria-label="Camera password" autoComplete="off" />
            </div>
          </Field>
          )}
          {found.some(c => !isOpen(c)) && Object.keys(camTypes).length > 0 && (
            <Field
              label="Camera brand (optional)"
              help="If you know it, pick it — connecting is quicker. Not sure? Leave it, we try them all."
            >
              <select className="gs-input" value={camType} onChange={e => setCamType(e.target.value)}>
                <option value="auto">I don’t know — try all</option>
                {Object.entries(camTypes).map(([k, v]) => (
                  <option key={k} value={k}>{v.label || k}</option>
                ))}
              </select>
            </Field>
          )}
          <button className="gs-btn primary" onClick={addAll}
                  disabled={adding || !!scanning || connectable === 0}>
            {adding ? <><span className="gs-spin" /> Connecting…</>
              : scanning ? 'Still searching…'
              : `Connect ${connectable} camera${connectable === 1 ? '' : 's'}`}
          </button>
        </>
      )}

      {!scanning && found !== null && found.length === 0 && cameras.length === 0 && (
        <div className="gs-empty">
          No cameras found on this network.<br />
          Check that your cameras are powered on and plugged into the same switch or
          router as this box, then search again.
        </div>
      )}

      {cameras.length > 0 && (
        <>
          <div className="gs-section-title">Your cameras</div>
          <p className="gs-help">
            Give each one the name you’d actually use — “Front Gate”, “Backyard”. That
            name is what your alerts will say.
          </p>
          {productHas('recordings') && (
            <p className="gs-help">
              Cameras added here don’t record yet — they still show live and send alerts.
              To record one, open <b>✏️ Edit camera</b> and tick <b>Recording enabled</b>.
            </p>
          )}
          <div className="gs-cams">
            {cameras.map(c => (
              <div className="gs-cam" key={c.idx}>
                <Snapshot idx={c.idx} refreshMs={5000} />
                <div className="gs-cam-body">
                  <input
                    className="gs-cam-name"
                    value={names[c.idx] !== undefined ? names[c.idx] : c.name}
                    onChange={e => rename(c.idx, e.target.value)}
                    onBlur={() => commitName(c.idx)}
                    maxLength={40}
                  />
                  {urlSourceLabel(c.url_source) && (
                    <div className="gs-cam-src" title={urlSourceLabel(c.url_source).long}>
                      {urlSourceLabel(c.url_source).short}
                    </div>
                  )}
                  <CameraStreamSettings idx={c.idx} onState={onStreamState} refreshKey={streamKey} />
                  {removing === c.idx ? (
                    <div className="gs-cam-remove-ask" role="alert">
                      <span>Remove “{c.name}” from this box? Its recordings stay until they expire.</span>
                      <div className="gs-cam-remove-btns">
                        <button className="gs-btn small danger" onClick={() => removeCam(c)} disabled={removeBusy}>
                          {removeBusy ? <><span className="gs-spin" /> Removing…</> : 'Remove'}
                        </button>
                        <button className="gs-btn small" onClick={() => setRemoving(null)} disabled={removeBusy}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                    {/* PRVSK-139: doorbell right here — no extra Guided Setup step. */}
                    <div className="gs-cam-bell">
                      <label htmlFor={`gs-bell-${c.idx}`}>🔔 Doorbell</label>
                      <select id={`gs-bell-${c.idx}`} className="gs-input gs-cam-bell-select"
                              value={bellOf(c)} onChange={e => setCamBell(c, e.target.value)}
                              title="A doorbell camera rings your phone with the person's photo, at any hour">
                        <option value="off">Off</option>
                        <option value="names">With names</option>
                        <option value="instant">Instant (1–2 s)</option>
                      </select>
                    </div>
                    {bellOf(c) !== 'off' && (
                      <div className="gs-cam-bell-area">
                        <span>📍 {hasGateArea(c)
                          ? 'Gate area set — only someone standing there rings it.'
                          : 'Where visitors stand: not set — anyone in view rings it.'}</span>
                        <button className="gs-btn small" onClick={() => { setEditTab('gate'); setEditCam(c); }}>
                          {hasGateArea(c) ? 'Change gate area' : 'Draw gate area'}
                        </button>
                      </div>
                    )}
                    <div className="gs-cam-actions">
                      <button className="gs-cam-remove" onClick={() => { setEditTab('basic'); setEditCam(c); }}
                              disabled={removeBusy} aria-label={`Edit ${c.name}`}>
                        ✏️ Edit camera
                      </button>
                      <button className="gs-cam-remove danger" onClick={() => setRemoving(c.idx)}
                              disabled={removeBusy} aria-label={`Remove ${c.name}`}>
                        🗑 Remove camera
                      </button>
                    </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {failed.length > 0 && (
        <Panel tone="warn" title={(() => {
          const n = failed.length, s = n > 1 ? 's' : '';
          if (failed.every(c => c.noPw)) return `⚠️ ${n} camera${s} need${n > 1 ? '' : 's'} a password`;
          if (failed.some(c => c.saveErr)) return `⚠️ ${n} camera${s} weren’t added`;
          if (failed.every(c => c.bad)) return `⚠️ ${n} camera${s} didn’t accept that password`;
          return `⚠️ ${n} camera${s} weren’t connected`;
        })()}>
          <p className="gs-help">Enter each one’s own login. Not sure of the brand? Leave it on “try all”.</p>
          {failed.map(c => (
            <div className="gs-retry" key={c.ip}>
              <div className="gs-retry-ip">{c.name || c.ip}</div>
              <input className="gs-input" placeholder="Username" aria-label={`Username for ${c.ip}`}
                     autoComplete="off" autoCapitalize="none" spellCheck={false}
                     value={c.user} disabled={c.busy}
                     onChange={e => patchFailed(c.ip, { user: e.target.value, bad: false })} />
              <PasswordInput className="gs-input" placeholder="Password" aria-label={`Password for ${c.ip}`} autoComplete="off"
                     value={c.password} disabled={c.busy}
                     onChange={e => patchFailed(c.ip, { password: e.target.value, bad: false })} />
              {Object.keys(camTypes).length > 0 && (
                <select className="gs-input" value={c.camType} disabled={c.busy}
                        onChange={e => patchFailed(c.ip, { camType: e.target.value })}>
                  <option value="auto">Brand: try all</option>
                  {Object.entries(camTypes).map(([k, v]) => (
                    <option key={k} value={k}>{v.label || k}</option>
                  ))}
                </select>
              )}
              <button className="gs-btn small primary" onClick={() => retryOne(c)} disabled={c.busy || !c.password}>
                {c.busy ? <><span className="gs-spin" /> Connecting…</> : 'Connect'}
              </button>
              {c.busy && prog[c.ip]?.state === 'running' && (
                <div className="gs-retry-prog">{prog[c.ip].label}{prog[c.ip].n ? ` · step ${prog[c.ip].n} of 4` : ''}</div>
              )}
              {c.bad && c.kind === 'auth' && (
                <div className="gs-retry-bad">Wrong username or password. We stopped after one try so the camera
                  doesn’t lock you out — nothing else was tried with it.</div>
              )}
              {c.bad && c.kind === 'locked' && (
                <div className="gs-retry-lock">
                  The camera says {c.attemptsLeft} attempt{c.attemptsLeft === 1 ? '' : 's'} left before it locks.
                  Check the password in the camera’s own app before trying again — a locked camera also stops
                  streaming until it unlocks.
                </div>
              )}
              {c.bad && !c.kind && <div className="gs-retry-bad">Didn’t connect — check the username and password, or try picking the brand.</div>}
              {c.saveErr && <div className="gs-retry-bad">Connected, but couldn’t save this camera ({c.saveErr}) — press Connect to try again.</div>}
            </div>
          ))}
          <button className="gs-btn small ghost" onClick={() => setFailed([])}
                  disabled={failed.some(c => c.busy)}>Skip these</button>
        </Panel>
      )}

      {note && <p className="gs-help gs-help-after">{note}</p>}
      {bellErr && <div className="gs-err">{bellErr}</div>}
      {err  && <div className="gs-err">{err}</div>}

      {cameras.length > 0 && !optAll && needIdx.length === 0 && refusedIdx.length > 0 && (
        <div className="gs-opt-all">
          <span>
            {refusedIdx.length} camera{refusedIdx.length > 1 ? 's' : ''} didn’t take the recommended settings over
            the network. If the box runs slowly, lower the resolution and frame rate in the camera’s own app or web page.
          </span>
        </div>
      )}

      {cameras.length > 0 && (needIdx.length > 0 || optAll) && (
        <div className={`gs-opt-all${reminded ? ' remind' : ''}`} role={reminded ? 'alert' : undefined}>
          <span>
            {optAll ? `Optimising ${optAll.done + 1 > optAll.total ? optAll.total : optAll.done + 1} of ${optAll.total}…`
              : reminded
                ? `${needIdx.length} camera${needIdx.length > 1 ? 's are' : ' is'} not optimised — the box may run slower and use more disk. Optimise now, or press Continue again to go on.`
                : `${needIdx.length} camera${needIdx.length > 1 ? 's' : ''} can be optimised for Parvekshak.`}
          </span>
          <button className="gs-btn small primary" onClick={optimiseAll} disabled={!!optAll}>
            {optAll ? <><span className="gs-spin" /> Optimising…</> : `Optimise all (${needIdx.length})`}
          </button>
        </div>
      )}

      {!scanning && (
        <p className="gs-cam-search-row">
          <button className="gs-btn small" onClick={() => scan()}>Search again</button>
          <button className="gs-btn small" onClick={() => setWizardOpen(true)}>+ Add Camera</button>
          {!adding && (
            <button className="gs-btn small" onClick={() => scan(true)}
                    title="Asks each device on your network directly. Slower, but finds cameras that did not answer the quick search.">
              Camera missing? Search deeper
            </button>
          )}
        </p>
      )}
      {deepNote && !scanning && <p className="gs-help">{deepNote}</p>}

      {/* PRVSK-156: cameras behind a second router. The default search never sweeps
          another network (a cheap router's NAT table chokes on it) — the installer
          asks for it here, pre-filled with what the scan saw behind another router. */}
      {!scanning && !adding && (
        <>
          {upstream.length > 0 && (
            <div className="gs-upstream">
              📡 Part of your network is behind another router:{' '}
              <b>{upstream.map(u => toOtherNet(u)?.label || u).join(', ')}</b>. Cameras plugged in
              there don't show up in the search above — search it too. It takes about a minute.
            </div>
          )}
          <form className="gs-cam-search-row gs-other-net"
                onSubmit={e => {
                  e.preventDefault();
                  const net = toOtherNet(otherNet);
                  if (!net) { setNetBad(true); setNetMiss(null); setDeepNote(null); return; }
                  scan(false, net);
                }}>
            <label htmlFor="gs-other-net">Cameras on another network?</label>
            <input id="gs-other-net" className="gs-input" inputMode="decimal" autoComplete="off"
                   placeholder="e.g. 192.168.1" value={otherNet}
                   onChange={e => { setOtherNet(e.target.value); setNetBad(false); }} />
            <button className="gs-btn small" type="submit" disabled={!otherNet.trim()}>Search</button>
          </form>
          {netBad && (
            <p className="gs-help">
              Enter the other network like <b>192.168.1</b> — the first three numbers of a camera's address.
            </p>
          )}
          {netMiss && (
            <div className="gs-err">
              No cameras answered on {netMiss}. If they're on a second router, this box usually can't
              reach them through it — set that router to <b>Access Point</b> (bridge) mode, or plug the
              cameras into the main router, then search again.
            </div>
          )}
        </>
      )}

      {/* The same Add Camera wizard as Settings → Cameras: by IP, NVR channel or RTSP URL. */}
      {wizardOpen && (
        <div className="adm-overlay" onClick={() => { setWizardOpen(false); reloadCameras(); }}>
          <div className="adm-modal gs-add-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3>Add Camera</h3>
              <button className="adm-modal-close" aria-label="Close"
                      onClick={() => { setWizardOpen(false); reloadCameras(); }}>✕</button>
            </div>
            <div className="gs-add-modal-body">
              <AddCameraWizard doneLabel="Done" recordingOff
                               onDone={() => { setWizardOpen(false); reloadCameras(); }} />
            </div>
          </div>
        </div>
      )}

      {editCam && (
        <EditCameraModal
          cam={editCam} idx={editCam.idx} initialTab={editTab}
          onClose={() => { setEditCam(null); reloadCameras(); }}
          onSaved={() => { setEditCam(null); setNames({}); reloadCameras(); }}
        />
      )}
    </>
  );
}

// ─── 4. Protected areas ───────────────────────────────────────────────────────
//
// The engine has no concept of an "included" area — it has exclusion_zones,
// which drop anyone whose centre falls inside them. So the question put to the
// customer is the inverse of the obvious one: not "what should we watch" but
// "what in this view isn't yours". That is the control that stops the street,
// the neighbour's terrace and the washing line generating alerts all night.

// PUT a camera change and throw a readable error when the box refuses it or can't be
// reached (PRVSK-112). These saves used to ignore the response: a failure looked like
// success, and a network error left the step "busy" with Continue disabled until a
// reload. A refused ignore area (422, PRVSK-108 #14) now shows its reason.
async function putCamera(idx, json) {
  let r;
  try {
    r = await apiFetch(`/cameras/${idx}`, { method: 'PUT', json });
  } catch {
    throw new Error('Could not reach the box. Check the connection and try again.');
  }
  if (!r.ok) {
    const d = await r.json().catch(() => null);
    throw new Error(typeof d?.detail === 'string' ? d.detail : 'Could not save. Try again.');
  }
}

// The same change on several cameras, one at a time; stops at the first failure and
// says how far it got.
async function putEachCamera(cams, json) {
  for (let i = 0; i < cams.length; i++) {
    try {
      await putCamera(cams[i].idx, json);
    } catch (e) {
      throw new Error(i ? `Saved on ${i} of ${cams.length} cameras, then: ${e.message}` : e.message);
    }
  }
}

// The hours the system may make noise — phone, siren and lights — as the box has
// them (GET /settings/noise-window). The home flow sets no hours of its own, so
// its screens must describe these rather than a made-up default (PRVSK-143).
// null until loaded.
function useNoiseWindow() {
  const [nw, setNw] = useState(null);
  useEffect(() => {
    let alive = true;
    apiFetch('/settings/noise-window')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setNw(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return nw;
}

// "between 00:30 and 05:00" / "at any hour" / null (muted or unknown).
function noiseHours(nw) {
  if (!nw) return null;
  if (nw.mode === 'always') return 'at any hour';
  if (nw.mode === 'off') return null;
  return `between ${nw.start} and ${nw.end}`;
}

// The same, for a camera's household answers (PUT /ui/setup/posture/{idx}).
async function putPosture(idx, json) {
  let r;
  try {
    r = await apiFetch(`/ui/setup/posture/${idx}`, { method: 'PUT', json });
  } catch {
    throw new Error('Could not reach the box. Check the connection and try again.');
  }
  if (!r.ok) {
    const d = await r.json().catch(() => null);
    throw new Error(typeof d?.detail === 'string' ? d.detail : 'Could not save. Try again.');
  }
}

// The onboarding screen. Everything the long-form Ignore-areas step does, minus
// what a self-installing customer should not be asked during setup:
//
//   Ask AI       sends a photo of their home to a third party. That cannot sit in
//                a fast consumer onboarding path, and the customer knows where
//                the road is better than any model does.
//   walk test    answers "can it see me", which matters AFTER setup, not during.
//   drawing      only offered to a camera that actually sees a road or a
//                neighbour. An ignore zone is only ever for "not my property";
//                fixtures on their own property are scene memory's job now.
function PerimeterStep(props) {
  return <AreasStep {...props} fast />;
}

function AreasStep({ setNav, onDone, cameras, reloadCameras, fast = false }) {
  const [sel,  setSel]  = useState(null);     // camera being drawn on
  // 'ignore' — never look here, or 'wait' — someone may stand still here.
  // Same canvas, same points, different destination. A seventh screen is not an
  // option: this flow is six on purpose ("ten screens is where they abandon").
  const [mode, setMode] = useState('ignore');
  const [shot, setShot] = useState(null);     // its snapshot URL (token-bearing)
  const [pts,  setPts]  = useState([]);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);
  // idx -> 'checking' | report. A view check takes ~15s (it needs frames spread
  // over time), so it is per-camera and opt-in rather than something that runs on
  // entering the step and makes the whole screen wait.
  const [checks, setChecks] = useState({});
  const canvasRef = useRef(null);
  const imgRef    = useRef(null);

  // Ask the server whether anything in this view looks like a person but never
  // moves. A decoration left in frame produced five false sirens at one site and
  // accounted for every alert that camera raised. An installer spots that; a
  // customer setting up their own system has no reason to suspect it, and the
  // symptom (random night alarms) looks like the product being bad rather than
  // like something they can fix in ten seconds by marking the spot.
  async function checkView(idx) {
    setChecks(s => ({ ...s, [idx]: 'checking' }));
    try {
      // apiFetch returns the raw Response — it does not parse. A non-ok status here
      // is the camera being unreadable, which is a thing to tell the customer, not
      // an exception to swallow.
      const res = await apiFetch(`/ui/setup/assess/${idx}`, { method: 'POST' });
      if (!res.ok) throw new Error(`assess ${res.status}`);
      const report = await res.json();
      setChecks(s => ({ ...s, [idx]: report }));
    } catch {
      setChecks(s => ({ ...s, [idx]: { verdict: 'error',
        message: 'Could not check this camera just now. You can carry on and try again later.' } }));
    }
  }
  // idx -> 'looking' | { zones, placement, objects } | { available:false }
  const [vision, setVision] = useState({});
  // Whether a relay exists to call at all. Asked once so the button is simply
  // absent on a box without one, rather than present and inert.
  const [aiOn, setAiOn] = useState(false);
  // Whether this customer has agreed to a view leaving the box. Separate from
  // aiOn: the button shows without it, and the first press is where we ask.
  const [consent, setConsent] = useState(false);
  // The camera whose press is waiting on that answer, or null.
  const [askFor, setAskFor] = useState(null);
  // Paid looks left for this site, ever. null until the box has been asked.
  // Camera indices are reused, so a per-camera allowance alone would hand out a
  // fresh free look every time a camera is removed and re-added.
  const [callsLeft, setCallsLeft] = useState(null);
  // idx -> 'never' | 'normal' | null. The household's own answer about each view.
  // "Does this camera see the road or a neighbour's place?"
  //
  // Asked INSTEAD of telling them to draw. Drawing was the hardest thing in the
  // flow and most cameras never needed it: an ignore zone is only ever for "not
  // my property", and a fixture on their own property is scene memory's job now.
  // So only the camera that answers yes is offered the drawing tool; the rest
  // skip it and lose nothing.
  const [publics, setPublics] = useState({});
  // idx -> true | false | undefined. Whether pets or strays come through this view.
  const [animals, setAnimals_] = useState({});
  // idx -> 'walking' | {seen, samples, message}. The walk test's last result.
  const [walks, setWalks] = useState({});
  // Boxes on older builds have neither route; their questions are left out there
  // rather than shown and failing on every answer.
  const ent = useEntitlements();
  const hasPosture = boxHas('setup-posture', ent);
  const hasWalk    = boxHas('setup-walk', ent);

  // "Stand where the camera can see you." Two stands, far and near, give this
  // camera's expected person height by image row — every size filter in the engine
  // is otherwise flat, judging a person at the top of frame and one at the bottom
  // against identical bands.
  //
  // It is framed as "can this camera see you?" because that is what the customer
  // actually wants to know, and it is the only check in the product that answers
  // it. A camera pointed at a wall or blinded by glare says so here rather than
  // after a burglary.
  async function walkTest(idx) {
    setWalks(w => ({ ...w, [idx]: 'walking' }));
    try {
      const res = await apiFetch(`/ui/setup/walk/${idx}`, { method: 'POST' });
      const d = res.ok ? await res.json()
                       : { seen: false, message: 'Could not check this camera just now.' };
      setWalks(w => ({ ...w, [idx]: d }));
    } catch {
      setWalks(w => ({ ...w, [idx]: { seen: false,
        message: 'Could not check this camera just now. You can carry on and try later.' } }));
    }
  }

  // Written straight through rather than batched into a Save. Each answer stands
  // alone, and a half-finished wizard should keep the answers already given — the
  // step people abandon is the one that asks for everything before anything counts.
  // Bulk setters for the "set all" panel. Each writes every armed camera and
  // then reloads once, rather than per camera: a twelve-camera site otherwise
  // reloads twelve times and the list visibly churns under the customer.
  // A refused or failed save used to leave the answer on screen as if it had been
  // kept (PRVSK-149). Now it says why and puts the previous answer back, so what
  // the customer sees is what the box has.
  async function setPublicAll(yes) {
    const before = publics;
    setPublics(p => { const n = { ...p }; armed.forEach(c => { n[c.idx] = yes; }); return n; });
    setErr(null);
    try {
      await putEachCamera(armed, { cam_config: { sees_public_area: yes } });
    } catch (e) {
      setPublics(before);
      setErr(e.message);
    }
    await reloadCameras().catch(() => {});
  }

  async function setPublic(idx, yes) {
    const before = publics[idx];
    setPublics(p => ({ ...p, [idx]: yes }));
    setErr(null);
    // Stored only so the question is not asked twice and the drawing tool knows
    // whether to appear. No detector reads it.
    try {
      await putCamera(idx, { cam_config: { sees_public_area: yes } });
      await reloadCameras().catch(() => {});
    } catch (e) {
      setPublics(p => ({ ...p, [idx]: before }));
      setErr(e.message);
    }
  }

  async function setAnimalsAll(value) {
    const before = animals;
    setAnimals_(a => { const n = { ...a }; armed.forEach(c => { n[c.idx] = value; }); return n; });
    setErr(null);
    // One at a time, like putEachCamera, so a failure says how far it got and the
    // cameras before it keep their answer.
    let done = 0;
    try {
      for (const c of armed) { await putPosture(c.idx, { animals_present: value }); done++; }
    } catch (e) {
      setAnimals_(a => { const n = { ...a }; armed.slice(done).forEach(c => { n[c.idx] = before[c.idx]; }); return n; });
      setErr(done ? `Saved on ${done} of ${armed.length} cameras, then: ${e.message}` : e.message);
    }
    await reloadCameras().catch(() => {});
  }

  async function setAnimals(idx, value) {
    const before = animals[idx];
    setAnimals_(a => ({ ...a, [idx]: value }));
    setErr(null);
    try {
      await putPosture(idx, { animals_present: value });
    } catch (e) {
      setAnimals_(a => ({ ...a, [idx]: before }));
      setErr(e.message);
    }
  }

  // Ask the service what it makes of this camera's view. Proposals only — nothing
  // is applied until the customer presses Add on a specific one. Marking zones is
  // the biggest lever on false alarms and the step people skip, and the reason
  // they skip it is the blank canvas, not the concept.
  useEffect(() => {
    apiFetch('/ui/setup/vision')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { setAiOn(!!(d && d.available)); setConsent(!!(d && d.consent)); })
      .catch(() => {});
  }, []);

  // Findings already stored on the box, loaded free on every visit. Without this
  // the results lived only in this component's state, so a reload lost them and
  // the only way back to them was another paid look at a question already
  // answered. A customer returning next week sees what was flagged, at no cost.
  useEffect(() => {
    apiFetch('/ui/setup/vision/results')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d && typeof d.calls_left === 'number') setCallsLeft(d.calls_left);
        const saved = (d && d.results) || {};
        if (!Object.keys(saved).length) return;
        setVision(v => {
          const next = { ...v };
          // Never clobber a result from this session — a fresh look is newer than
          // whatever was on disk when the page opened.
          for (const k of Object.keys(saved)) if (next[k] === undefined) next[k] = saved[k];
          return next;
        });
      })
      .catch(() => {});
  }, []);

  // Consent is asked at the first press rather than as a checkbox at the start,
  // because here the customer can see exactly which camera and what it is for.
  // Granting it does NOT send anything — it unblocks the press that follows.
  async function allowVision() {
    const idx = askFor;
    setAskFor(null);
    try {
      const res = await apiFetch('/ui/setup/vision/consent',
                                 { method: 'PUT', json: { consent: true } });
      if (!res.ok) return;
      setConsent(true);
      if (idx != null) suggest(idx);
    } catch { /* leave consent off; the manual flow is unaffected */ }
  }

  // `force` sends another photograph of the property and costs another call, so
  // it is only ever passed from a deliberate "Look again" press. Without it the
  // box returns what it already has, free, marked `cached`.
  async function suggest(idx, force = false) {
    setVision(v => ({ ...v, [idx]: 'looking' }));
    try {
      const res = await apiFetch(`/ui/setup/vision/${idx}${force ? '?force=true' : ''}`,
                                 { method: 'POST' });
      const data = res.ok ? await res.json() : { available: false };
      setVision(v => ({ ...v, [idx]: data }));
      // Only a real look costs one. `cached` replies and failures spend nothing,
      // and the box is the authority — this just keeps the button honest until
      // the next load rather than guessing.
      if (data && data.cached === false && data.available) {
        setCallsLeft(n => (typeof n === 'number' ? Math.max(0, n - 1) : n));
      } else if (data && data.reason === 'call_limit') {
        setCallsLeft(0);
      }
    } catch {
      setVision(v => ({ ...v, [idx]: { available: false } }));
    }
  }

  // Open a proposed region IN THE EDITOR, drawn on the customer's own frame,
  // instead of applying it on one tap.
  //
  // An ignored area is a permanent blind spot. It was being created by a single
  // press on a text row — "There's a tree trunk in view · Ignore it" — with the
  // shape never shown. The proposals are also two different kinds of claim: the
  // local check reports something it MEASURED (seen in 7 of 10 frames at one
  // spot), while the model reports something it thinks WILL look like a person
  // after dark and which may never have raised an alert in its life. Neither
  // deserves to blind a camera sight-unseen, and the second one especially.
  //
  // The editor already takes normalised points and already appends on save, so
  // this costs the customer one extra press and shows them exactly what they are
  // giving up.
  async function previewZone(idx, polygon) {
    const url = await apiImageUrl(`/cameras/${idx}/frame?t=${Date.now()}`);
    setShot(url);
    setPts(polygon);
    setSel(idx);
  }

  // "Watching" is detect_mode, NOT enabled. enabled:false tears the camera down
  // entirely — no worker, no live view; detect_mode:'off' is the record-only
  // state the customer actually means by "don't alert me about this one".
  const isArmed = c => (c.detect_mode || 'full') !== 'off';
  const armed   = cameras.filter(isArmed);

  // Seed the answers from what the box already holds, so revisiting this step shows
  // what was said rather than an unanswered question the customer answers twice.
  useEffect(() => {
    setPublics(p => {
      const next = { ...p };
      cameras.forEach(c => {
        if (next[c.idx] === undefined && c.cam_config?.sees_public_area !== undefined) {
          next[c.idx] = !!c.cam_config.sees_public_area;
        }
      });
      return next;
    });
    setAnimals_(a => {
      const next = { ...a };
      let changed = false;
      for (const c of cameras) {
        if (next[c.idx] === undefined && typeof c.animals_present === 'boolean') {
          next[c.idx] = c.animals_present; changed = true;
        }
      }
      return changed ? next : a;
    });
  }, [cameras]);

  // Older installs store a zone as a rectangle {x1,y1,x2,y2} rather than a
  // polygon. Reading only `polygon` would show them as "nothing ignored" AND
  // silently drop them on the next save — re-enabling alerts on a street some
  // installer had already excluded.
  const waitZonesOf = c => (c.cam_config?.wait_zones || [])
    .map(z => (Array.isArray(z?.polygon) ? z.polygon : null)).filter(Boolean);
  const zonesOf = c => (c.cam_config?.exclusion_zones || []).map(z => {
    if (Array.isArray(z?.polygon) && z.polygon.length >= 3) return z.polygon;
    if (z && z.x1 != null) return [[z.x1, z.y1], [z.x2, z.y1], [z.x2, z.y2], [z.x1, z.y2]];
    return null;
  }).filter(Boolean);

  async function setArmed(idx, on) {
    setErr(null);
    try {
      await putCamera(idx, { detect_mode: on ? 'full' : 'off' });
    } catch (e) {
      setErr(e.message);
    } finally {
      await reloadCameras().catch(() => {});
    }
  }

  // Redraw: saved zones filled grey, the one being drawn in white.
  useEffect(() => {
    const cv = canvasRef.current, im = imgRef.current;
    if (!cv || !im || sel === null) return;
    const cam = cameras.find(c => c.idx === sel);
    if (!cam) return;
    const draw = () => {
      cv.width = im.naturalWidth || 1280;
      cv.height = im.naturalHeight || 720;
      const g = cv.getContext('2d');
      g.clearRect(0, 0, cv.width, cv.height);
      g.drawImage(im, 0, 0, cv.width, cv.height);
      // The canvas runs at the camera's native resolution (often ~2300 px wide)
      // but is shown at ~1000 px, so sizes in canvas pixels shrink ~2.5×. Scale
      // them to the on-screen size, or a 6 px dot ends up ~2 px and invisible.
      const k = cv.width / (cv.clientWidth || cv.width);
      const poly = (p, fill, stroke, close = true) => {
        if (p.length < 2) return;
        g.beginPath();
        p.forEach(([x, y], i) => (i ? g.lineTo(x * cv.width, y * cv.height)
                                   : g.moveTo(x * cv.width, y * cv.height)));
        if (close) { g.closePath(); g.fillStyle = fill; g.fill(); }
        g.lineJoin = 'round';
        g.strokeStyle = 'rgba(0,0,0,0.6)'; g.lineWidth = 5 * k; g.stroke();   // dark halo: shows on white walls
        g.strokeStyle = stroke;            g.lineWidth = 2.5 * k; g.stroke();
      };
      zonesOf(cam).forEach(p => poly(p, 'rgba(0,0,0,0.55)', 'rgba(255,255,255,0.85)'));
      // The area being drawn: a filled shape from 3 corners, a plain line before that.
      poly(pts, 'rgba(255,214,0,0.25)', '#FFD600', pts.length >= 3);
      pts.forEach(([x, y]) => {
        g.beginPath();
        g.arc(x * cv.width, y * cv.height, 7 * k, 0, Math.PI * 2);
        g.fillStyle = '#FFD600'; g.fill();
        g.strokeStyle = '#000'; g.lineWidth = 2 * k; g.stroke();
      });
    };
    if (im.complete) draw(); else im.onload = draw;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, pts, cameras, shot]);

  const click = e => {
    const r = e.currentTarget.getBoundingClientRect();
    setPts(p => [...p, [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height]]);
  };

  async function saveZone() {
    if (pts.length < 3 || sel === null) return;
    setBusy(true);
    const cam = cameras.find(c => c.idx === sel);
    const next = [...zonesOf(cam), pts].map(p => ({ polygon: p }));
    // zone_overlap_min: match on AREA as well as centroid, for zones drawn here.
    // Centroid alone leaks the case these zones exist to stop — a tall box (a branch,
    // a person on the far side of a wall) can have its centre outside the shape while
    // most of it is inside. Measured on a site camera: three false alerts covered
    // 90-95% of their box against at most 46% for eleven real ones, so 0.6 separates
    // them with a wide margin. The area test is an OR applied after the centroid test,
    // so it can only reject more — which is why it is set only on zones a customer
    // draws in setup, never applied retroactively to already-tuned cameras.
    setErr(null);
    try {
      if (mode === 'wait') {
        // No zone_overlap_min here. That exists to make an IGNORE zone catch more
        // (a tall box mostly inside it), and the same generosity on a waiting area
        // would exempt a fixture standing just outside a door.
        const waitNext = [...waitZonesOf(cam), pts].map(p => ({ polygon: p }));
        await putCamera(sel, { cam_config: { wait_zones: waitNext } });
      } else {
        await putCamera(sel, { cam_config: { exclusion_zones: next, zone_overlap_min: 0.6 } });
      }
      setPts([]);          // kept on failure, so the area can be fixed and saved again
    } catch (e) {
      setErr(e.message);
    } finally {
      await reloadCameras().catch(() => {});
      setBusy(false);
    }
  }

  async function clearZones(idx) {
    setBusy(true);
    setErr(null);
    try {
      await putCamera(idx, { cam_config: { exclusion_zones: [] } });
    } catch (e) {
      setErr(e.message);
    } finally {
      await reloadCameras().catch(() => {});
      setBusy(false);
    }
  }

  const proceed = useCallback(async () => {
    await onDone({ armed: armed.length });
    return true;
  }, [onDone, armed.length]);

  useEffect(() => {
    // How many cameras have not been told whether they see a road or a
    // neighbour. Never BLOCKS continuing: an unanswered camera simply behaves
    // normally, and holding someone in setup over an optional question loses
    // them entirely.
    const unanswered = fast ? armed.filter(c => publics[c.idx] === undefined).length : 0;
    setNav({ canNext: armed.length > 0 && !busy, onNext: proceed,
             note: armed.length === 0 ? 'Switch on at least one camera to continue'
                 : unanswered ? `${unanswered} of ${armed.length} cameras not answered yet — you can still continue`
                 : null });
  }, [setNav, armed, armed.length, busy, proceed, fast, publics]);

  const selCam = sel !== null ? cameras.find(c => c.idx === sel) : null;

  if (selCam) {
    return (
      <>
        <h1 className="gs-h1">
          {mode === 'wait' ? 'Where might someone stand still?'
                           : 'What should this camera ignore?'}
        </h1>
        {mode === 'wait' ? (
          <p className="gs-lede">
            On <strong>{selCam.name}</strong>, draw around anywhere a person could stop and
            wait — a door or gate someone is let in through, a porch, a reception desk,
            beside parked cars. Somebody standing still in these areas raises an alarm
            even when the camera can barely make them out. <strong>This adds alerts, it
            does not remove them</strong> — a person loitering by a car is the one you
            least want to miss.
          </p>
        ) : (
          <p className="gs-lede">
            On <strong>{selCam.name}</strong>, draw around anything outside your property —
            the road, a neighbour’s gate, a balcony — and around anything that moves on its
            own, like a tree, a bush or a washing line. Nothing in those areas will raise
            an alarm.
          </p>
        )}

        <Panel title="How to draw">
          Tap each corner of the area, then press <strong>Save this area</strong>. Three
          corners is enough, and you can mark more than one.{' '}
          {mode === 'wait'
            ? 'If nobody ever stops and waits anywhere in this view, skip it.'
            : 'If this view has nothing that belongs to someone else and nothing that sways in the wind, skip it.'}
        </Panel>

        <div className="gs-canvas-wrap">
          <img ref={imgRef} alt="" style={{ display: 'none' }} src={shot || undefined} />
          <canvas ref={canvasRef} className="gs-canvas" onClick={click} />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="gs-btn primary" onClick={saveZone} disabled={pts.length < 3 || busy}>
            Save this area ({pts.length} corner{pts.length === 1 ? '' : 's'})
          </button>
          <button className="gs-btn" onClick={() => setPts([])} disabled={!pts.length}>Start over</button>
          {zonesOf(selCam).length > 0 && (
            <button className="gs-btn" onClick={() => clearZones(selCam.idx)} disabled={busy}>
              Remove all ignored areas
            </button>
          )}
          <button className="gs-btn ghost" onClick={() => { setPts([]); setSel(null); setShot(null); }}>Done with this camera</button>
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className="gs-h1">Which cameras should we watch?</h1>
      <p className="gs-lede">
        Switch on the cameras you want alerts from. A camera that is off still shows
        live — it just won’t raise an alarm.
      </p>

      {cameras.map(c => (
        <Toggle
          key={c.idx}
          on={isArmed(c)}
          onChange={v => setArmed(c.idx, v)}
          title={c.name}
          sub={zonesOf(c).length
            ? `${zonesOf(c).length} area ignored on this camera`
            : 'Watching the whole view'}
        />
      ))}

      {/* Two reasons to mark an area, and the second one is the bigger cause of
          false alarms — but a customer only ever volunteers the first. They think of
          a hedge as scenery, not as something that moves, so it has to be named.
          The fix is a zone and not a threshold: foliage_threshold rejects on edge
          variance, and a person in a patterned shirt or standing in front of a bush
          scores high too. That is Guard post — "foreground all shrubbery", eighteen
          real people missed. */}
      <div className="gs-section-title">Mark what shouldn’t raise an alarm</div>
      <p className="gs-help">
        <strong>Anything that isn’t yours.</strong> The street, a neighbour’s door, a
        passage you don’t own — otherwise every passer-by becomes an alert.
      </p>
      <p className="gs-help">
        <strong>Anything that moves on its own.</strong> A tree or bush that sways,
        hanging laundry, a flag, a curtain, a swing. These are what set the alarm off
        on a windy night, and they are the ones people forget.
      </p>
      <p className="gs-help">
        Between them this is the single biggest cause of false alarms. Two minutes
        here saves a lot of them.
      </p>
      <p className="gs-help">
        Press <strong>Check</strong> on a camera and we’ll watch that view for a few
        seconds and point out anything already there that looks like a person — a
        statue, a bicycle, a pale ornament that lights up at night. Clear the view of
        people first.
      </p>

      {/* Asked here, at the press, rather than as a checkbox at the start of setup.
          A permission sought where the person can see which camera it applies to
          and what it is for is one they actually read. Says plainly what leaves
          the box, because "AI assistance" does not tell anyone that a photograph
          of their home is about to be sent somewhere. */}
      {askFor !== null && (
        <Panel title="This sends one photo off your box">
          <p className="gs-help" style={{ marginTop: 0 }}>
            To point out what might be mistaken for a person, we take one still from{' '}
            <strong>{(armed.find(c => c.idx === askFor) || {}).name || 'this camera'}</strong>{' '}
            and send it to our service to be looked at once. It isn’t kept, and no video
            or live feed is sent. Everything else in this step works without it.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="gs-btn primary small" onClick={allowVision}>
              Allow and look at this camera
            </button>
            <button className="gs-btn ghost small" onClick={() => setAskFor(null)}>
              Not now
            </button>
          </div>
        </Panel>
      )}

      {/* ANSWER ONCE, THEN FIX THE EXCEPTIONS.
          A home with twelve cameras faces thirty-six dropdowns here, which is
          where somebody stops reading and answers everything the same way by
          accident. And the accident is usually right: a property almost always
          has ONE answer with one or two exceptions — every camera says "nobody
          at night" except the front door.
          So set the whole site in one press, then override the odd one below.
          Hidden under four cameras, where it is just another control to read. */}
      {fast && armed.length >= 4 && (
        <Panel>
          <div className="gs-row-name" style={{ marginBottom: 8 }}>
            Set all {armed.length} cameras at once
          </div>
          {hasPosture && <div className="gs-row-sub" style={{ display: 'block', marginBottom: 10 }}>
            Are there animals around?{' '}
            <Pick onChange={v => setAnimalsAll(v)}
                  options={[{ value: true,  label: 'Pets or strays' },
                            { value: false, label: 'No animals' }]} />
          </div>}
          <div className="gs-row-sub" style={{ display: 'block' }}>
            Do any of these see the road or a neighbour's place?{' '}
            <Pick onChange={v => setPublicAll(v)}
                  options={[{ value: false, label: 'All mine' },
                            { value: true,  label: "I'll mark them below" }]} />
          </div>
          <p className="gs-note" style={{ marginTop: 10 }}>
            Then change any camera that is different, below.
          </p>
        </Panel>
      )}

      {/* ONE row per camera, not one per action. This step used to render four
          separate per-camera lists - mark, check, findings, suggestions - so a ten
          camera home scrolled past forty rows with each camera's name repeated four
          times, and had to hold in their head which list they were in. Everything a
          camera needs now sits on its own line, with findings indented beneath it. */}
      {armed.map(c => {
        const chk  = checks[c.idx];
        const vis  = vision[c.idx];
        const nz   = zonesOf(c).length;
        const busyNow = chk === 'checking' || vis === 'looking';
        const found = [...((chk && chk !== 'checking' && chk.phantoms) || []).map(ph => {
                        const b = ph.box || [];
                        return { key: `ph${ph.cell}`, what: 'Something that never moves',
                                 sub: `Seen in ${ph.frames_seen} of ${ph.of_frames} checks, always in the same spot.`,
                                 poly: b.length === 4
                                   ? [[b[0],b[1]],[b[2],b[1]],[b[2],b[3]],[b[0],b[3]]] : null };
                      }),
                      // Worded as the prediction it is. The old line — "It never
                      // moves, so it can set the alarm off night after night" —
                      // stated as fact something that has not happened: a static
                      // object cannot alert on its own, because the pipeline needs
                      // motion, a path and direction. The one case that did (a
                      // decorative star, five sirens) needed an insect moving in
                      // front of it AND a 35s window where the motion mask was
                      // None AND the night-confidence door. Real, and rare.
                      // Telling a customer it happens nightly pushes them to blind
                      // a working camera against something that may never occur.
                      ...((vis && vis.objects) || []).map((o, i) => (
                        { key: `ob${i}`, what: `${o.what}`,
                          // "It has not caused an alert" was asserted unconditionally,
                          // and was false: it was shown about a decoration that had
                          // already caused one and sounded a siren. The model cannot
                          // know either way — the box can, from its own alert history
                          // per grid cell, so the claim is only made when it is true.
                          sub: o.alerts_here > 0
                            ? 'Could be mistaken for a person at night, when infrared makes pale things glow.'
                            : 'Could be mistaken for a person at night, when infrared makes pale things glow. It has not caused an alert — worth knowing, not necessarily worth ignoring.',
                          alerts_here: o.alerts_here || 0,
                          people_here: o.people_here,
                          poly: (o.polygon || []).length >= 3 ? o.polygon : null }))];

        // A failed check is NOT a clean view, and saying so is the whole point.
        // These rows used to collapse "the service could not be reached" into
        // "Nothing found", because {available:false} is truthy and fell through to
        // the same branch. A customer read that as the camera having been looked at
        // and found clear, and moved on without marking anything — the exact
        // outcome this step exists to prevent. Seen live: the relay was returning
        // 503 and the wizard reported every camera as clean.
        const aiFailed  = !!(vis && vis !== 'looking' && vis.available === false);
        const chkFailed = !!(chk && chk !== 'checking' && chk.verdict === 'error');

        // The box already says WHY it failed; this used to read only `available`
        // and print "AI check didn't run", so an out-of-credit relay looked
        // identical to a dead camera and sent people hunting the wrong fault.
        const aiWhy = !aiFailed ? '' : ({
          not_enabled:     'not switched on',
          no_snapshot:     'the camera gave no picture',
          no_image:        'the camera gave no picture',
          image_too_large: 'the picture was too big to send',
          unreachable:     'the service could not be reached',
          call_limit:      'this site has used all its AI looks',
        }[vis && vis.reason] || (String((vis && vis.reason) || '').startsWith('http_')
          ? 'the service refused the request'
          : ''));

        // When this view was last looked at. A stored result costs nothing to show,
        // so the honest thing is to say when it was taken rather than let it read
        // as if it just ran.
        const aiAge = (vis && vis !== 'looking' && typeof vis.age_days === 'number')
          ? (vis.age_days < 1 ? 'today'
             : vis.age_days < 2 ? 'yesterday'
             : `${Math.round(vis.age_days)} days ago`)
          : '';
        const aiStale = !!(vis && vis !== 'looking' && vis.stale);

        // Areas the AI suggests ignoring (a public road, a neighbour's gate). The box
        // always passed these through, but nothing rendered them, so the feature's
        // main output never reached the customer (PRVSK-114). Shown as suggestions:
        // "Show me" draws the shape on the view and nothing is ignored until Save.
        const suggested = (vis && vis !== 'looking' && Array.isArray(vis.zones) ? vis.zones : [])
          .filter(z => Array.isArray(z.polygon) && z.polygon.length >= 3);
        const toLook = found.length + suggested.length;

        const aiFailMsg = `AI check didn’t run${aiWhy ? ` — ${aiWhy}` : ''}`;
        const seen      = aiAge ? ` · looked at ${aiAge}${aiStale ? ', worth a fresh look' : ''}` : '';

        const status =
          busyNow                      ? 'Looking at this view…'
          : nz && toLook               ? `${nz} area${nz === 1 ? '' : 's'} ignored · ${toLook} to look at${seen}`
          : toLook                     ? `${toLook} thing${toLook === 1 ? '' : 's'} to look at${seen}`
          : aiFailed                   ? (nz ? `${nz} area${nz === 1 ? '' : 's'} ignored · ${aiFailMsg}`
                                             : `${aiFailMsg} — nothing was looked at`)
          : chkFailed                  ? (nz ? `${nz} area${nz === 1 ? '' : 's'} ignored · check didn’t run`
                                             : 'Check didn’t run — nothing was looked at')
          : nz                         ? `${nz} area${nz === 1 ? '' : 's'} ignored`
          : (chk || vis)               ? 'Nothing found'
          : 'Not checked yet';

        const animal      = animals[c.idx];
        const walk        = walks[c.idx];
        const walkSamples = (walk && walk !== 'walking' && walk.samples) || 0;

        return (
          <div key={c.idx}>
            <div className="gs-row">
              <span className="gs-row-icon">{found.length ? '⚠️' : busyNow ? '⏳' : '📷'}</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{c.name}</span>
                <span className="gs-row-sub">{status}</span>
                {/* Replaces "draw your ignore areas". The customer knows his own
                    street; no model does. Only a yes needs the drawing tool. */}
                <span className="gs-row-sub" style={{ display: 'block', marginTop: 4 }}>
                  Does this camera see the road, or a neighbour's place?{' '}
                  <Pick value={publics[c.idx]} onChange={v => setPublic(c.idx, v)}
                        options={[{ value: false, label: 'All mine' },
                                  { value: true,  label: 'Some is not mine' }]} />
                  {publics[c.idx] &&
                    <em style={{ marginLeft: 6 }}>mark it below, and nothing there is ever looked at</em>}
                </span>
                {/* Per camera, because it genuinely differs: a yard the neighbour's
                    cat crosses nightly and a first-floor balcony it cannot reach
                    need different settings, and nothing in the image decides it. */}
                {hasPosture && <span className="gs-row-sub" style={{ display: 'block', marginTop: 4 }}>
                  Are there animals here?{' '}
                  <Pick value={animal} onChange={v => setAnimals(c.idx, v)}
                        options={[{ value: true,  label: 'Pets or strays' },
                                  { value: false, label: 'No animals' }]} />
                </span>}
                {/* Two stands, far then near, teach this camera how big a person
                    looks where. Pitched as the question the customer cares about:
                    whether the camera can see them at all.

                    "All of you in the picture" is not a nicety. A box clipped by
                    the frame edge has no foot point and a height that is only the
                    visible part, so it is refused — and on a camera angled down
                    into a garden the NEAR stand is exactly where that happens. */}
                {!fast && hasWalk && <span className="gs-row-sub" style={{ display: 'block', marginTop: 4 }}>
                  <button className="gs-btn small" disabled={walk === 'walking'}
                          onClick={() => walkTest(c.idx)}>
                    {walk === 'walking' ? 'Looking for you…'
                      : walkSamples ? 'Stand somewhere else' : 'Can it see me?'}
                  </button>
                  <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 12 }}>
                    Stand where all of you is in the picture — once far, once near.
                  </span>
                  {walk && walk !== 'walking' &&
                    <span style={{ marginLeft: 8 }}>
                      {/* stored, not seen: a clipped stand IS seen and still useless */}
                      {walk.stored ? '✓ ' : '⚠️ '}{walk.message}
                      {walkSamples ? ` (${walkSamples} measurement${walkSamples === 1 ? '' : 's'})` : ''}
                    </span>}
                </span>}
              </span>
              {(!fast || publics[c.idx] || nz) && (
                <button className="gs-btn small" disabled={busy}
                  onClick={async () => {
                    // /frame, not /snapshot: an <img> sends the token as ?token=, which only /frame accepts.
                    const url = await apiImageUrl(`/cameras/${c.idx}/frame?t=${Date.now()}`);
                    setShot(url); setPts([]); setSel(c.idx); setMode('ignore');
                  }}>
                  {nz ? 'Edit' : 'Mark'}
                </button>
              )}
              {/* Replaces the yes/no question that used to sit on this row.
                  Customers state where people DO wait — a door, a gate, a porch,
                  beside parked cars — and never where they do not, so only the
                  positive is asked for. Marking one here makes that spot MORE
                  sensitive, not less: inside it a person who resolves no pose
                  still alerts, which is the loitering-at-a-car case. */}
              <button className="gs-btn small" disabled={busy}
                onClick={async () => {
                  const url = await apiImageUrl(`/cameras/${c.idx}/frame?t=${Date.now()}`);
                  setShot(url); setPts([]); setSel(c.idx); setMode('wait');
                }}>
                {waitZonesOf(c).length ? `Waiting areas (${waitZonesOf(c).length})` : 'Where do people wait?'}
              </button>
              {/* Separate buttons on purpose. The first watches this view from the
                  box and costs nothing; the second sends a photograph of the
                  property to the model and costs a little, so it should be a
                  deliberate press rather than something bundled into "Check". */}
              {!fast && (
                <button className="gs-btn small" disabled={busyNow}
                        onClick={() => checkView(c.idx)}>
                  {chk && chk !== 'checking' ? 'Check again' : 'Check'}
                </button>
              )}
              {/* A stored finding is shown free on load, so pressing this again on a
                  camera already looked at must be a DELIBERATE choice to send another
                  photograph — hence force, and a label that says so. "Ask AI again"
                  read like a refresh and was pressed as one. */}
              {!fast && aiOn && (() => {
                const already = !!(vis && vis !== 'looking' && vis.available !== false);
                // Every press spends: a first look, or a forced second one. Stored
                // findings keep showing regardless — only the button is blocked.
                const spent = callsLeft === 0;
                return (
                  <button className="gs-btn small" disabled={busyNow || spent}
                          title={spent ? 'This site has used all its AI looks'
                                 : already ? 'Sends another photo of this view'
                                 : undefined}
                          onClick={() => {
                            if (!consent) { setAskFor(c.idx); return; }
                            suggest(c.idx, already);
                          }}>
                    {already ? 'Look again' : 'Ask AI'}
                  </button>
                );
              })()}
            </div>

            {/* Setup happens while the household is going about its day, so the
                view is often not empty. One still frame cannot separate a person
                standing still from a mannequin, and on a live corridor camera this
                returned a shape described as "the pale doorway and wall at the far
                end, where the person is standing now" — a proposal to blind the
                doorway somebody was standing in. The findings are still shown,
                because they may be right; what changes is that nobody should press
                "Show me" on them without re-running on an empty view first. */}
            {vis && vis !== 'looking' && vis.people_present && ((vis.objects || []).length > 0 || suggested.length > 0) && (
              <div className="gs-row" style={{ paddingLeft: 24 }}>
                <span className="gs-row-icon">⚠️</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">Someone was in view when we looked</span>
                  <span className="gs-row-sub">
                    These findings may include a person rather than an object. Clear the
                    view and press Ask AI again before ignoring any of them.
                  </span>
                </span>
              </div>
            )}

            {found.map(f => (
              <div className="gs-row" key={`${c.idx}-${f.key}`} style={{ paddingLeft: 24 }}>
                <span className="gs-row-icon">🗿</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{f.what}</span>
                  <span className="gs-row-sub">{f.sub} Moving it is better than ignoring it.</span>
                  {/* What actually happened at this spot, from the box's own alert
                      history. The model can only guess at risk — it wrote "It has
                      not caused an alert" about a decoration that had caused one
                      and sounded a siren. The box knows; the model cannot. */}
                  {f.alerts_here > 0 &&
                    <span className="gs-row-sub" style={{ display: 'block' }}>
                      ⚠️ This spot has already set off {f.alerts_here} alert
                      {f.alerts_here === 1 ? '' : 's'}
                      {f.people_here === 0 ? ', and a person was never found there' : ''}.
                    </span>}
                </span>
                {f.poly && (
                  <button className="gs-btn small" disabled={busy}
                          onClick={() => previewZone(c.idx, f.poly)}>Show me</button>
                )}
              </div>
            ))}

            {suggested.map((z, i) => (
              <div className="gs-row" key={`${c.idx}-sz${i}`} style={{ paddingLeft: 24 }}>
                <span className="gs-row-icon">🚧</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">Suggested area to ignore: {z.label}</span>
                  <span className="gs-row-sub">
                    {z.why ? `${z.why} ` : ''}({z.confidence} confidence) Press Show me to see it
                    on the view — nothing is ignored until you press Save.
                  </span>
                </span>
                <button className="gs-btn small" disabled={busy}
                        onClick={() => previewZone(c.idx, z.polygon)}>Show me</button>
              </div>
            ))}

            {vis && vis.placement && vis.placement.grade !== 'good' && (
              <div className="gs-row" style={{ paddingLeft: 24 }}>
                <span className="gs-row-icon">{vis.placement.grade === 'poor' ? '⚠️' : 'ℹ️'}</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">
                    {vis.placement.grade === 'poor'
                      ? 'This camera may struggle' : 'This camera could be better placed'}
                  </span>
                  <span className="gs-row-sub">{vis.placement.advice}</span>
                </span>
              </div>
            )}
          </div>
        );
      })}

      {err && <div className="gs-err" role="alert">{err}</div>}
    </>
  );
}

// ─── 5. How people behave here ────────────────────────────────────────────────
//
// Sets one per-camera flag, osc_from_origin, for views where people stay a while.
//
// The system rejects "oscillating" tracks — something swaying in place, like a
// plant — by comparing how far a track ended up from where it started against how
// far it travelled in total. Without this flag that comparison uses the oldest
// point still in a fixed-length history buffer rather than the true origin, and
// the tracker's own note spells out the consequence: the ratio "falls below
// OSC_NET_PATH after ~140 frames for ANY track, however straight it travels".
//
// So on a camera where somebody stands and talks, waits at a door, or works in
// view, a real person eventually gets written off as a swaying plant — and the
// longer they stay, the more certain the system becomes that they are scenery.
//
// This is a correctness fix rather than a sensitivity setting, which is why the
// question can be asked in plain language and the answer is a boolean. It is
// opt-in per camera in the engine, pending field data, so the wizard only sets it
// where the customer says people actually linger.
const LINGER_ON  = { osc_from_origin: true };
const LINGER_OFF = { osc_from_origin: null };

function LingerStep({ setNav, onDone, onSkip, cameras, reloadCameras }) {
  const isArmed = c => (c.detect_mode || 'full') !== 'off';
  const armed   = cameras.filter(isArmed);
  const lingers = c => !!(c.cam_config?.osc_from_origin);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);

  // Most homes answer the same way for every camera, so the common case is one
  // tap rather than one per camera. Sequential rather than parallel: ten cameras
  // is ten PUTs, and each reloads the worker's config - firing them at once on a
  // four-core box is a burst with nothing gained, since nobody is watching the
  // individual rows flip.
  async function setAllLinger(on) {
    setBusy(true);
    setErr(null);
    try {
      await putEachCamera(armed, { cam_config: on ? LINGER_ON : LINGER_OFF });
    } catch (e) {
      setErr(e.message);
    } finally {
      await reloadCameras().catch(() => {});
      setBusy(false);
    }
  }

  async function setLinger(idx, on) {
    setBusy(true);
    setErr(null);
    try {
      await putCamera(idx, { cam_config: on ? LINGER_ON : LINGER_OFF });
    } catch (e) {
      setErr(e.message);
    } finally {
      await reloadCameras().catch(() => {});
      setBusy(false);
    }
  }

  const proceed = useCallback(async () => {
    await onDone({ cams: cameras.filter(lingers).length });
    return true;
  }, [cameras, onDone]);

  useEffect(() => {
    setNav({ canNext: !busy, onNext: proceed, nextLabel: 'Next', onSkip });
  }, [setNav, busy, proceed, onSkip]);

  return (
    <>
      <h1 className="gs-h1">Do people stay a while in any of these views?</h1>
      <p className="gs-lede">
        Switch this on where someone might stand and talk, wait at a door, park and
        unload, or work in view for several minutes. Leave it off where people simply
        walk past.
      </p>
      <p className="gs-help">
        Not sure? Skip this — you can come back once you have seen how the cameras
        behave for a few days.
      </p>

      {armed.length === 0 && (
        <p className="gs-help">No cameras are set to alert yet. You can come back to this later.</p>
      )}

      {armed.length > 1 && (
        <Toggle
          on={armed.every(lingers)}
          onChange={setAllLinger}
          title="All cameras"
          sub={armed.every(lingers)
            ? 'People stay a while everywhere'
            : 'Set every camera at once, then change any below'}
        />
      )}

      {armed.map(c => (
        <Toggle
          key={c.idx}
          on={lingers(c)}
          onChange={v => setLinger(c.idx, v)}
          title={c.name}
          sub={lingers(c) ? 'People stay a while here' : 'People walk past'}
        />
      ))}
      {err && <div className="gs-err" role="alert">{err}</div>}
    </>
  );
}

// ─── 6. Pets & animals ────────────────────────────────────────────────────────
//
// The one detection question a home customer can answer better than we can, and
// the one whose wrong answer is most obviously the product's fault: a cat crossing
// the garden at 2am wakes the house, every night, until they unplug it.
//
// Two per-camera settings, no numbers shown:
//   animal_check_always   — run the animal check on CONFIRMED tracks too, not only
//                           the day/no-face path. The day path needs nothing set,
//                           so this is the one that matters: a dog that survives
//                           two frames is promoted to a confirmed track and rides
//                           that branch untouched without it.
//   animal_memory_secs    — remember an animal seen at that spot for a few seconds,
//                           so one cat walking through does not re-trigger per frame
//
// animal_person_override is DELIBERATELY NOT SET, having been here at 0.75 and
// measured on a ten-camera site: across 10 animal-check events, every camera
// without it rejected correctly, at person-confidence 0.755 to 0.883. Cats and
// dogs score 0.75-0.88 as a person on real cameras, so a "definitely a person"
// bar at 0.75 sits below the entire observed animal range. The one camera that
// did carry it let two animals through as person alerts — visible in telemetry as
// check-ran, animal found above the reject bar, nothing rejected.
//
// It is a real safety valve at a value above what animals score; we do not yet
// know what that value is, and guessing it low is how a dog becomes an alert.
// Unset, every path keeps its own built-in bar — which is what the cameras that
// work are running.
const ANIMAL_ON  = { animal_check_always: true,  animal_memory_secs: 8, animal_person_override: null };
const ANIMAL_OFF = { animal_check_always: null,  animal_memory_secs: null, animal_person_override: null };

function AnimalsStep({ setNav, onDone, onSkip, cameras, reloadCameras }) {
  const isArmed = c => (c.detect_mode || 'full') !== 'off';
  const armed   = cameras.filter(isArmed);
  const hasAnimals = c => !!(c.cam_config?.animal_check_always);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);

  async function setAllAnimals(on) {
    setBusy(true);
    setErr(null);
    try {
      await putEachCamera(armed, { cam_config: on ? ANIMAL_ON : ANIMAL_OFF });
    } catch (e) {
      setErr(e.message);
    } finally {
      await reloadCameras().catch(() => {});
      setBusy(false);
    }
  }

  async function setAnimals(idx, on) {
    setBusy(true);
    // null clears a per-camera override — see the PUT /cameras/{idx} merge, where an
    // explicit null pops the key so the camera inherits the global again. Writing
    // false instead would pin it off forever.
    setErr(null);
    try {
      await putCamera(idx, { cam_config: on ? ANIMAL_ON : ANIMAL_OFF });
    } catch (e) {
      setErr(e.message);
    } finally {
      await reloadCameras().catch(() => {});
      setBusy(false);
    }
  }

  const proceed = useCallback(async () => {
    await onDone({ cams: cameras.filter(hasAnimals).length });
    return true;
  }, [cameras, onDone]);

  useEffect(() => {
    setNav({ canNext: !busy, onNext: proceed, nextLabel: 'Next', onSkip });
  }, [setNav, busy, proceed, onSkip]);

  return (
    <>
      <h1 className="gs-h1">Do pets or animals come through?</h1>
      <p className="gs-lede">
        Switch this on for any camera where a cat, dog, cow or monkey might wander
        past. We’ll stop them setting off the alarm — while still alerting you about
        a person, even if an animal was there a moment earlier.
      </p>

      {armed.length === 0 && (
        <p className="gs-help">No cameras are set to alert yet. You can come back to this later.</p>
      )}

      {/* The sub-line says what the SETTING does, not what the system is. The old
          "Alerting on anything that moves" read as though ticking the box turned
          alerts ON for animals — the opposite of its effect — and was not accurate
          either: the system alerts on what it believes are people, and this check
          exists for the narrower case of an animal being mistaken for one. */}
      {armed.length > 1 && (
        <Toggle
          on={armed.every(hasAnimals)}
          onChange={setAllAnimals}
          title="All cameras"
          sub={armed.every(hasAnimals)
            ? 'Animals ignored everywhere'
            : 'Set every camera at once, then change any below'}
        />
      )}

      {armed.map(c => (
        <Toggle
          key={c.idx}
          on={hasAnimals(c)}
          onChange={v => setAnimals(c.idx, v)}
          title={c.name}
          sub={hasAnimals(c)
            ? 'Animals won’t trigger alerts here'
            : 'An animal could trigger a false alert'}
        />
      ))}

      <p className="gs-help gs-help-after">
        Leave this off for a camera that only sees a doorway or a stairwell — there is
        nothing for it to help with, and every check costs a little processing.
      </p>
      {err && <div className="gs-err" role="alert">{err}</div>}
    </>
  );
}

// ─── Industrial: AI models ────────────────────────────────────────────────────
//
// Two questions on one screen, because they are one question asked twice: WHERE
// a model runs, and HOW it is tuned.
//
// Model-first, not camera-first. A plant has one answer per model — "PPE on the
// three shop-floor cameras" — and twenty cameras each carrying a list of models
// is the same information arranged so that nobody can check it.
//
// Settings are the SITE default (GLOBAL_DETECTOR_CONFIG), which every camera
// resolves UNDER its own overrides — so one number tunes a whole plant. A camera
// that genuinely needs to differ (the one indoor PPE camera on a CPU-starved box)
// is set from the same panel, by switching scope to that camera, and the screen
// shows which cameras differ rather than letting a site default look like it
// governs a camera it does not.

// What the models chosen here will cost the box.
//
// Shown because the alternative is finding out later. These are CPU-only boxes
// running every camera on the same few cores, so a model's cost is per camera and
// four models across eight cameras is a different machine from the one the site
// was sized for. The number is an ESTIMATE and says so — only the PPE pass has a
// measured cost — but the shape of it is right, and it is the difference between
// a choice and a surprise.
//
// Half the cores are treated as already spoken for: decoding every stream, plus
// the person and face pipeline that runs regardless of what is ticked here.
function LoadMeter({ load, cores }) {
  const budget = Math.max(1, cores || 2) / 2;
  const pct    = load / budget;
  const tone   = pct > 1 ? 'bad' : pct > 0.7 ? 'warn' : '';
  return (
    <>
      <div className="gs-meter">
        <div className={`gs-meter-fill ${tone}`} style={{ width: `${Math.min(100, pct * 100)}%` }} />
      </div>
      <p className="gs-help" style={{ marginTop: 0 }}>
        Rough estimate: <strong>{load.toFixed(1)}</strong> of about{' '}
        <strong>{budget.toFixed(1)}</strong> CPU-seconds of work per second of video
        {cores ? ` (this box has ${cores} cores; half are left for decoding the streams
                   and the people/faces the system always runs)` : ''}.
      </p>
      {pct > 1 && (
        <Panel tone="bad" title="This is more than the box can keep up with">
          Cameras will fall behind and alerts will arrive late. Drop a model from some
          cameras, or raise “Seconds between scans” — scanning every 6 seconds instead of
          every 3 halves the cost and still catches someone who is not wearing a helmet.
        </Panel>
      )}
      {pct > 0.7 && pct <= 1 && (
        <Panel tone="warn" title="This is close to the limit">
          It should run, with little room left. Watch the frame rate on the Cameras page
          for a day before adding more.
        </Panel>
      )}
    </>
  );
}

function ModelsStep({ setNav, onDone }) {
  const [data,   setData]   = useState(null);    // GET /ui/setup/models
  const [assign, setAssign] = useState({});      // { modelKey: [camIdx, ...] }
  const [tune,   setTune]   = useState({});      // { modelKey: { setting: value } } — site
  const [ppe,    setPpe]    = useState(['helmet']);
  const [open,   setOpen]   = useState(null);    // model whose settings are expanded
  const [scope,  setScope]  = useState({});      // { modelKey: 'site' | camIdx }
  // Per-camera edits only — { camIdx: { modelKey: { setting: value | null } } }.
  // A null clears that camera's override so it follows the site default again
  // (the PUT /cameras merge pops a null key).
  const [camTune, setCamTune] = useState({});
  const [err,    setErr]    = useState(null);
  // Identity, per camera. Three states, kept as two lists rather than one
  // tri-state control, because the third state is real and must not be silently
  // resolved: a camera can recognise faces, alert on any person without checking
  // who, or do NEITHER — which is right for a camera that exists only to count
  // boxes over a conveyor, where person alerts would be pure noise. Forcing such
  // a camera into "alert on any person" because this screen was opened would be
  // a regression nobody asked for, so untouched cameras stay as they are.
  const [faceCams,    setFaceCams]    = useState([]);
  const [instantCams, setInstantCams] = useState([]);
  const [ahCams,      setAhCams]      = useState([]);   // after-hours
  const [ahClose,     setAhClose]     = useState('18:00');
  const [ahGuards,    setAhGuards]    = useState([]);   // allowed after closing
  const [known,       setKnown]       = useState([]);   // enrolled faces

  useEffect(() => {
    apiFetch('/ui/setup/models')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d) { setErr('Could not read the camera list. Check the connection and reload.'); return; }
        setData(d);
        const a = {}, t = {};
        MODELS.forEach(m => {
          a[m.key] = (d.cameras || []).filter(c => (c.detectors || []).includes(m.key))
                                      .map(c => c.idx);
          t[m.key] = withDefaults(m.key, (d.defaults || {})[m.key]);
        });
        setAssign(a);
        setTune(t);
        // PPE items: the site default if there is one, otherwise whatever a camera
        // is already enforcing, so opening this step never silently narrows a
        // site that was set up on the AI Models page.
        const site = ((d.defaults || {}).helmet || {}).ppe_items;
        const cam  = (d.cameras || [])
          .map(c => ((c.detector_config || {}).helmet || {}).ppe_items)
          .find(x => Array.isArray(x) && x.length);
        setPpe(Array.isArray(site) && site.length ? site : (cam || ['helmet']));
        // Identity, read off what each camera is actually running.
        const has = (c, k) => (c.detectors || []).includes(k);
        setFaceCams((d.cameras || []).filter(c => FACE_ON_SET.some(k => has(c, k))).map(c => c.idx));
        setInstantCams((d.cameras || []).filter(c => has(c, FACE_OFF_DETECTOR)).map(c => c.idx));
        setAhCams((d.cameras || []).filter(c => has(c, 'after_hours')).map(c => c.idx));
        const ah = withDefaults('after_hours', (d.defaults || {}).after_hours);
        setAhClose(ah.closing_time);
        const g = ((d.defaults || {}).after_hours || {}).guard_names;
        setAhGuards(Array.isArray(g) ? g : []);
      })
      .catch(() => setErr('Could not read the camera list. Check the connection and reload.'));
  }, []);

  // Enrolled faces, for "who may be here after closing".
  useEffect(() => {
    apiFetch('/faces/known')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setKnown((d?.persons || []).map(p => p.name)))
      .catch(() => {});
  }, []);

  // Memoised: the save callback below depends on it, and a fresh [] each render
  // would rebuild that callback every time, which re-fires setNav in a loop.
  const cams      = useMemo(() => data?.cameras || [], [data]);
  const inventory = Object.fromEntries((data?.models || []).map(m => [m.key, m]));
  const chosen    = MODELS.filter(m => (assign[m.key] || []).length);

  const toggleCam = (key, idx) => setAssign(a => {
    const on = (a[key] || []).includes(idx);
    return { ...a, [key]: on ? a[key].filter(i => i !== idx) : [...(a[key] || []), idx] };
  });
  const setAll  = (key, all) => setAssign(a => ({ ...a, [key]: all ? cams.map(c => c.idx) : [] }));
  const setTune_ = (key, k, v) => setTune(t => ({ ...t, [key]: { ...(t[key] || {}), [k]: v } }));

  // ── per-camera overrides ──
  // What a camera is ALREADY saved with, as the box last reported it.
  const savedOverride = (idx, model) =>
    ((cams.find(c => c.idx === idx)?.detector_config || {})[model]) || {};
  // What a camera is running once this screen's unsaved edits are counted.
  const camValue = (idx, model, k) => {
    const edit = camTune[idx]?.[model];
    if (edit && k in edit) return edit[k] === null ? tune[model]?.[k] : edit[k];
    const saved = savedOverride(idx, model);
    return k in saved ? saved[k] : tune[model]?.[k];
  };
  const camDiffers = (idx, model, k) => {
    const edit = camTune[idx]?.[model];
    if (edit && k in edit) return edit[k] !== null;
    return k in savedOverride(idx, model);
  };
  const setCamTune_ = (idx, model, k, v) => setCamTune(c => ({
    ...c, [idx]: { ...(c[idx] || {}), [model]: { ...((c[idx] || {})[model] || {}), [k]: v } },
  }));
  // Hand one camera back to the site default: every key of that model nulled, so
  // the save pops them and the camera inherits again.
  const clearCamTune = (idx, model) => setCamTune(c => ({
    ...c,
    [idx]: {
      ...(c[idx] || {}),
      [model]: Object.fromEntries((MODEL_SETTINGS[model] || []).map(s => [s.key, null])),
    },
  }));
  // Cameras differing from the site default for this model, counting edits.
  const overridingCams = model => cams.filter(c =>
    (MODEL_SETTINGS[model] || []).some(s => camDiffers(c.idx, model, s.key)));

  // The load estimate reads each camera's OWN effective settings, so turning the
  // image size down on one camera shows up in the total rather than being hidden
  // behind the site default. Defined here, after camValue, not before it.
  const camSettings = (idx, model) =>
    Object.fromEntries((MODEL_SETTINGS[model] || []).map(s => [s.key, camValue(idx, model, s.key)]));
  const load = MODELS.reduce((sum, m) => sum + (assign[m.key] || [])
    .reduce((s, idx) => s + modelLoad(m.key, camSettings(idx, m.key)), 0), 0);

  const save = useCallback(async () => {
    setErr(null);
    // Site settings are sent only for models actually in use, and within those,
    // only values that DIFFER from the detector's own default — see nonDefault.
    // Writing every slider at its displayed value is how a screen's idea of a
    // default becomes a stored override nobody chose.
    const defaults = {};
    Object.entries(assign).forEach(([k, list]) => {
      if (!(list || []).length || !MODEL_SETTINGS[k]) return;
      const diff = nonDefault(k, tune[k], (data?.defaults || {})[k]);
      if (Object.keys(diff).length) defaults[k] = diff;
    });
    // PPE items: only when they are not the shipped single-helmet default, or
    // when something is already stored and has to be replaced.
    if ((assign.helmet || []).length) {
      const items  = ppe.length ? ppe : ['helmet'];
      const stored = ((data?.defaults || {}).helmet || {}).ppe_items;
      const isDef  = items.length === 1 && items[0] === 'helmet';
      if (!isDef || stored) {
        defaults.helmet = { ...(defaults.helmet || {}), ppe_items: isDef && !stored ? null : items };
      }
    }
    // Identity expands into the detectors it is actually made of. Sent in the
    // same request as the rest of the matrix so a site is never half-switched.
    const ident = {
      unknown: faceCams, known: faceCams, banned: faceCams,
      [FACE_OFF_DETECTOR]: instantCams,
      after_hours: ahCams,
    };
    if (ahCams.length) {
      const diff = nonDefault('after_hours', { closing_time: ahClose },
                              (data?.defaults || {}).after_hours);
      const storedG = ((data?.defaults || {}).after_hours || {}).guard_names;
      if (ahGuards.length)  diff.guard_names = ahGuards;
      else if (storedG)     diff.guard_names = null;
      if (Object.keys(diff).length) defaults.after_hours = diff;
    }

    const r = await apiFetch('/ui/setup/models',
                             { method: 'PUT', json: { assign: { ...assign, ...ident }, defaults } });
    if (!r.ok) {
      const d = await r.json().catch(() => null);
      setErr(typeof d?.detail === 'string' ? d.detail : 'Could not save. Try again.');
      return false;
    }
    // Per-camera overrides go to the camera itself — one PUT each, and only for
    // cameras actually edited on this screen. Done after the matrix so a camera
    // that just gained a model already has it when its override lands.
    for (const [idx, byModel] of Object.entries(camTune)) {
      const dc = {};
      Object.entries(byModel).forEach(([model, keys]) => {
        if (Object.keys(keys || {}).length) dc[model] = keys;
      });
      if (!Object.keys(dc).length) continue;
      const cr = await apiFetch(`/cameras/${idx}`, { method: 'PUT', json: { detector_config: dc } });
      if (!cr.ok) {
        const d = await cr.json().catch(() => null);
        const name = cams.find(c => c.idx === Number(idx))?.name || `camera ${idx}`;
        setErr(typeof d?.detail === 'string'
          ? `${name}: ${d.detail}`
          : `Saved the site settings, but ${name}’s own settings did not save. Try again.`);
        return false;
      }
    }
    const placed = Object.values(assign).reduce((n, l) => n + (l || []).length, 0);
    await onDone({ models: Object.keys(assign).filter(k => (assign[k] || []).length).length,
                   placements: placed });
    return true;
  }, [assign, tune, ppe, camTune, cams, data, faceCams, instantCams, ahCams, ahClose,
      ahGuards, onDone]);

  // Flip one camera between recognising faces and person-only. The two lists are
  // kept mutually exclusive here rather than at save time, so what the screen
  // shows is always what will be written.
  const setFace = (idx, on) => {
    setFaceCams(l => (on ? [...new Set([...l, idx])] : l.filter(i => i !== idx)));
    // Face off normally leaves `instant` so the camera still alerts on a person.
    // A camera already running something that alerts on its own — a zone_watch
    // zone — does not need it, and on a corridor it is wrong: residents walk past
    // all day and `instant` fires on each one, burying the loitering alert that is
    // the whole point. See needsFallbackAlerter.
    const cam  = (cams || []).find(c => c.idx === idx);
    const rest = (cam?.detectors || []).filter(k => !FACE_AWARE.includes(k));
    const keep = on || !needsFallbackAlerter(rest);
    setInstantCams(l => (keep ? l.filter(i => i !== idx) : [...new Set([...l, idx])]));
  };

  useEffect(() => {
    setNav({ canNext: !!data, onNext: save,
             note: data ? null : 'Loading your cameras…' });
  }, [setNav, data, save]);

  return (
    <>
      <h1 className="gs-h1">Which AI models, on which cameras?</h1>
      <p className="gs-lede">
        Every camera already watches for people. These are the extra models — pick each
        one and tap the cameras it belongs on. A model costs processing on every camera
        you put it on, so put it only where it has something to find.
      </p>

      {!data && !err && <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>}

      {cams.length === 0 && data && (
        <div className="gs-empty">No cameras yet — add them on the Cameras step first.</div>
      )}

      {/* ── Identity, before anything else ──
          Whether a camera works out WHO it is looking at decides what every alert
          off it means, so it is the first question on the screen rather than a
          switch on a tuning page. */}
      {cams.length > 0 && (
        <>
          <div className="gs-section-title">Who is this?</div>
          <div className="gs-row" style={{ borderBottom: 'none', paddingBottom: 2 }}>
            <span className="gs-row-icon">{IDENTITY.face.icon}</span>
            <span className="gs-row-text">
              <span className="gs-row-name">{IDENTITY.face.title}</span>
              <span className="gs-row-sub">{IDENTITY.face.on}</span>
            </span>
          </div>
          <div className="gs-chips">
            {cams.map(c => (
              <button key={c.idx} type="button"
                      className={`gs-chip${faceCams.includes(c.idx) ? ' on' : ''}`}
                      onClick={() => setFace(c.idx, !faceCams.includes(c.idx))}>
                {c.name}
                {!c.enabled && <span className="gs-chip-off">off</span>}
              </button>
            ))}
          </div>

          {instantCams.length > 0 && (
            <p className="gs-help">
              <strong>
                {cams.filter(c => instantCams.includes(c.idx)).map(c => c.name).join(', ')}
              </strong>{' '}
              {instantCams.length === 1 ? 'alerts' : 'alert'} on any person without checking
              who — {IDENTITY.face.off.toLowerCase()}
            </p>
          )}

          {/* The third state, named rather than quietly resolved. */}
          {(() => {
            const neither = cams.filter(c => !faceCams.includes(c.idx)
                                          && !instantCams.includes(c.idx));
            if (!neither.length) return null;
            return (
              <Panel tone="warn" title={`${neither.length} camera${neither.length === 1 ? '' : 's'} raise no person alerts`}>
                <strong>{neither.map(c => c.name).join(', ')}</strong> {neither.length === 1 ? 'is' : 'are'} not
                watching for people at all. That is right for a camera that only counts
                things, and wrong for anything guarding a door — tap it above to switch
                it on. Nothing was changed for you.
              </Panel>
            );
          })()}

          <div className="gs-row" style={{ borderBottom: 'none', paddingBottom: 2, marginTop: 18 }}>
            <span className="gs-row-icon">{IDENTITY.after_hours.icon}</span>
            <span className="gs-row-text">
              <span className="gs-row-name">{IDENTITY.after_hours.title}</span>
              <span className="gs-row-sub">{IDENTITY.after_hours.desc}</span>
            </span>
          </div>
          <div className="gs-chips">
            {cams.map(c => (
              <button key={c.idx} type="button"
                      className={`gs-chip${ahCams.includes(c.idx) ? ' on' : ''}`}
                      onClick={() => setAhCams(l => l.includes(c.idx)
                        ? l.filter(i => i !== c.idx) : [...l, c.idx])}>
                {c.name}
              </button>
            ))}
          </div>

          {ahCams.length > 0 && (
            <>
              {askSettings('after_hours').map(s => (
                <SettingRow key={s.key} spec={s} value={ahClose}
                            onChange={v => setAhClose(v)} />
              ))}
              {known.length > 0 && (
                <Field
                  label="Who may still be here after that?"
                  help="Night guards and anyone on a late shift. They are recognised and let through; everybody else raises an alert."
                >
                  <div className="gs-chips">
                    {known.map(n => (
                      <button key={n} type="button"
                              className={`gs-chip${ahGuards.includes(n) ? ' on' : ''}`}
                              onClick={() => setAhGuards(l => l.includes(n)
                                ? l.filter(x => x !== n) : [...l, n])}>
                        {n}
                      </button>
                    ))}
                  </div>
                </Field>
              )}
              {/* Honest about the overlap rather than letting someone set up a
                  feature that cannot do anything on that camera. */}
              {ahCams.some(i => instantCams.includes(i)) && (
                <Panel tone="warn" title="This adds nothing on some of those cameras">
                  <strong>
                    {cams.filter(c => ahCams.includes(c.idx) && instantCams.includes(c.idx))
                         .map(c => c.name).join(', ')}
                  </strong>{' '}
                  already alerts on any person at any hour, so an after-hours rule changes
                  nothing there. It earns its place on a camera that recognises faces,
                  where staff are normally let through.
                </Panel>
              )}
            </>
          )}

          <div className="gs-section-title">What else should it look for?</div>
        </>
      )}

      {cams.length > 0 && MODELS.map(m => {
        const on   = assign[m.key] || [];
        const info = inventory[m.key];
        const dead = info && (info.exists === false || info.failed);
        // Cameras that differ from the site default for this model, unsaved edits
        // included. Named rather than counted: a site default that a camera
        // ignores is a lie of omission, and which camera is the useful half.
        const odd = overridingCams(m.key);
        return (
          <div key={m.key} style={{ marginBottom: 26 }}>
            <div className="gs-row" style={{ borderBottom: 'none', paddingBottom: 2 }}>
              <span className="gs-row-icon">{m.icon}</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{m.label}</span>
                <span className="gs-row-sub">
                  {on.length ? `On ${on.length} of ${cams.length} cameras` : 'Off everywhere'} · {m.desc}
                </span>
              </span>
              {cams.length > 1 && (
                <button className="gs-btn small" onClick={() => setAll(m.key, on.length !== cams.length)}>
                  {on.length === cams.length ? 'None' : 'All'}
                </button>
              )}
              {tunedSettings(m.key).length > 0 && on.length > 0 && (
                <button className="gs-btn small" onClick={() => setOpen(o => (o === m.key ? null : m.key))}>
                  {open === m.key ? 'Hide settings' : 'Settings'}
                </button>
              )}
            </div>

            {/* A model whose weights are missing can never run. Said here, where it
                is being chosen, rather than discovered as silence later. */}
            {dead && (
              <Panel tone="bad" title="This model cannot run on this box">
                {info.filename} {info.exists === false ? 'is not installed' : 'failed to load'}.
                Your installer needs to add it before this model will do anything.
              </Panel>
            )}

            <div className="gs-chips">
              {cams.map(c => (
                <button key={c.idx} type="button"
                        className={`gs-chip${on.includes(c.idx) ? ' on' : ''}`}
                        onClick={() => toggleCam(m.key, c.idx)}>
                  {c.name}
                  {!c.enabled && <span className="gs-chip-off">off</span>}
                </button>
              ))}
            </div>

            {on.length > 0 && odd.length > 0 && open !== m.key && (
              <p className="gs-help">
                {odd.map(c => c.name).join(', ')}{' '}
                {odd.length === 1 ? 'has its own settings' : 'have their own settings'} for this
                model. Press Settings to see or change them.
              </p>
            )}

            {/* A question no default can answer is asked on the card itself, never
                behind Advanced: "is this camera outdoors" is not a tuning knob,
                and a customer who never opens Settings would never see it. */}
            {on.length > 0 && askSettings(m.key).map(s => (
              <SettingRow key={s.key} spec={s} value={tune[m.key]?.[s.key]}
                          onChange={v => setTune_(m.key, s.key, v)} />
            ))}

            {open === m.key && (() => {
              const sc    = scope[m.key] ?? 'site';
              const site  = sc === 'site';
              const specs = MODEL_SETTINGS[m.key] || [];
              const vals  = site ? (tune[m.key] || {})
                                 : Object.fromEntries(specs.map(s => [s.key, camValue(sc, m.key, s.key)]));
              return (
                <div className="gs-panel">
                  <div className="gs-panel-title">Settings for {m.label}</div>
                  {/* Scope, not a separate screen. One number tunes the plant; a
                      camera that must differ is one chip away, and the chip says
                      which cameras already do. */}
                  {on.length > 1 && (
                    <div className="gs-chips">
                      <button type="button" className={`gs-chip${site ? ' on' : ''}`}
                              onClick={() => setScope(s => ({ ...s, [m.key]: 'site' }))}>
                        Whole site
                      </button>
                      {cams.filter(c => on.includes(c.idx)).map(c => (
                        <button key={c.idx} type="button"
                                className={`gs-chip${sc === c.idx ? ' on' : ''}`}
                                onClick={() => setScope(s => ({ ...s, [m.key]: c.idx }))}>
                          {c.name}
                          {odd.some(o => o.idx === c.idx) && <span className="gs-chip-off">own</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="gs-help" style={{ marginTop: 0 }}>
                    {site
                      ? 'Applies to every camera above that has no setting of its own.'
                      : `Just this camera. Anything you leave alone follows the site setting.`}
                  </p>
                  {/* Everything here already has an answer that is right nearly
                      everywhere, so it is shown as "what it is set to", not as a
                      question — and only what differs from the shipped default is
                      ever written. The count is the useful signal: a site with
                      nothing changed is a site running what we tested. */}
                  {(() => {
                    const changed = specs.filter(s => {
                      const v = vals[s.key];
                      return v !== undefined && v !== null && v !== s.def;
                    });
                    return (
                      <p className="gs-help">
                        {changed.length === 0
                          ? 'All at their shipped defaults — nothing here is stored until you change it.'
                          : `${changed.length} changed from the default: `
                            + changed.map(s => s.label.toLowerCase()).join(', ')}
                      </p>
                    );
                  })()}
                  {specs.map(s => (
                    <SettingRow key={s.key} spec={s} value={vals[s.key]}
                                overridden={!site && camDiffers(sc, m.key, s.key)}
                                onChange={v => (site ? setTune_(m.key, s.key, v)
                                                     : setCamTune_(sc, m.key, s.key, v))} />
                  ))}
                  {!site && camDiffers(sc, m.key, specs[0]?.key) !== undefined
                        && specs.some(s => camDiffers(sc, m.key, s.key)) && (
                    <button className="gs-btn small ghost"
                            onClick={() => clearCamTune(sc, m.key)}>
                      Use the site settings on this camera
                    </button>
                  )}
                  <p className="gs-help">
                    Costs about {modelLoad(m.key, vals).toFixed(2)} CPU-seconds per second on{' '}
                    {site ? 'each camera' : 'this camera'}, at these settings.
                  </p>
                </div>
              );
            })()}

            {/* PPE is the one model where the question "which gear?" is the whole
                configuration, so it sits with the model rather than two steps away. */}
            {m.key === 'helmet' && on.length > 0 && (
              <>
                <div className="gs-section-title">Gear to enforce</div>
                <p className="gs-help">
                  A worker with the body part showing and no gear on it raises a PPE alert.
                  Helmet is the one proven on site footage; the others use the same model
                  and are not yet validated here.
                </p>
                <div className="gs-chips">
                  {PPE_ITEMS.map(it => (
                    <button key={it.key} type="button"
                            className={`gs-chip${ppe.includes(it.key) ? ' on' : ''}`}
                            onClick={() => setPpe(p => p.includes(it.key)
                              ? p.filter(x => x !== it.key) : [...p, it.key])}>
                      {it.icon} {it.label}{it.proven ? '' : ' *'}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}

      {cams.length > 0 && (
        <>
          <div className="gs-section-title">What this costs the box</div>
          <LoadMeter load={load} cores={data?.cores || 0} />
          {chosen.length === 0 && (
            <p className="gs-help">
              Nothing extra switched on. That is a valid answer — person and face detection
              run on every camera regardless, and these models can be added later from the
              AI Models page.
            </p>
          )}
        </>
      )}

      {err && <div className="gs-err" role="alert">{err}</div>}
    </>
  );
}

// ─── Industrial: site modules ─────────────────────────────────────────────────
//
// Attendance, Guard Patrol and access correlation. Each is its own module with
// its own page; what this step does is switch one on and give it the ONE thing
// it cannot work without, so it is running by the time the installer leaves.
//
// The honesty problem this step exists to solve: attendance and patrol are both
// driven by a ZONE, and a checkpoint with no zone drawn is skipped outright by
// the engine (attendance.py: `if cp.get("zones") and _in_window(...)`). So a
// module can be switched on, show a checkpoint in its list, and never once fire.
// That is why the box reports `configured` and `ready` separately and why this
// screen refuses to call a zone-less checkpoint set up.
//
// Anything beyond the first checkpoint — more checkpoints on a patrol route,
// shift schedules, the employee directory, PACS event import — belongs on the
// module's own page, and this step links there rather than growing into it.

// The three modules, each with the panel that configures it IN FULL. Setup does
// not hand the installer off to another screen halfway through: an industrial
// site is commissioned in one sitting, often by somebody who will not be back,
// and "finish this on the Guard Patrol page" is how a site ends up with a module
// switched on and never configured.
const SITE_MODULES = [
  { key: 'attendance', icon: '🕘', title: 'Attendance', Body: AttendancePanel,
    sub: 'Clock staff in and out by face, at the gate or the door',
    what: 'check-in point' },
  { key: 'patrol', icon: '🛡️', title: 'Guard patrol', Body: PatrolPanel,
    sub: 'Check a guard walked the round, and tell a supervisor when they did not',
    what: 'checkpoint' },
  { key: 'pacs', icon: '🔑', title: 'Badge checking', Body: PacsPanel,
    sub: 'Compare who badged in against who the camera saw at that door',
    what: 'door' },
  // No switch below this line: these are per-camera detectors, on where they are
  // set up and off where they are not. Giving them a master toggle would invent a
  // state the config does not have.
  { key: 'access', icon: '🚪', title: 'Who is allowed where', Body: AccessPanel,
    sub: 'Name the people who belong at a camera, and anyone else raises an alert there',
    what: 'restricted camera', always: true },
  { key: 'zones', icon: '⛔', title: 'Restricted areas', Body: ZonesPanel,
    sub: 'An area nobody should stand in, or a line nobody should cross',
    what: 'camera', always: true },
  { key: 'zone_watch', icon: '🗺', title: 'Watched zones', Body: ZoneWatchPanel,
    sub: 'Several named zones on one camera — a line nobody crosses, somewhere nobody belongs, or somewhere nobody should linger',
    what: 'camera', always: true },
  { key: 'counting', icon: '🔢', title: 'Counting and footfall', Body: CountingPanel,
    sub: 'Count people or vehicles across a line, in each direction',
    what: 'camera', always: true },
  { key: 'shopanalytics', icon: '🛒', title: 'Shop analytics', Body: ShopAnalyticsPanel,
    sub: 'How long customers stayed, time at the counter, shelf reaches — ready the next morning',
    what: 'camera', always: true },
  { key: 'seats', icon: '💺', title: 'Seat occupancy', Body: SeatsPanel,
    sub: 'Report which seats were taken, and for how long',
    what: 'camera', always: true },
];

// Why a configured thing will never fire, said in that module's own terms. All of
// them come down to the same cause — nothing drawn, so there is no test to run —
// but "a place with no area" is wrong for a counting line and reads as a bug.
const MODULE_WARN = {
  _zone:    'A place with no area drawn on it is skipped — the system has no way to tell '
          + 'when somebody is standing there.',
  pacs:     'A door with no camera cannot be compared against anything.',
  zones:    'A camera switched on with nothing drawn has no area to guard.',
  counting: 'A camera switched on with no line drawn has nothing to count across.',
  seats:    'A camera switched on with no seats marked has nothing to report on.',
  shopanalytics: 'A camera switched on with no doorway or areas drawn has nothing to measure.',
};

function ModulesStep({ setNav, onDone, onSkip, cameras }) {
  const [mods, setMods] = useState(null);
  const [openKey, setOpen] = useState(null);   // module being set up, full width
  const [busy, setBusy]    = useState(false);
  const [err,  setErr]     = useState(null);

  // Modules watch people, so a camera that raises no alerts is still a fair place
  // for a checkpoint — but offer the alerting ones first, since that is what the
  // customer has just been thinking about.
  const armed   = cameras.filter(c => (c.detect_mode || 'full') !== 'off');
  const camOpts = (armed.length ? armed : cameras)
    .map(c => ({ idx: c.idx, name: c.name, enabled: c.enabled !== false }));

  const load = useCallback(() => apiFetch('/ui/setup/modules')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setMods(d); })
    .catch(() => setErr('Could not read the module list.')), []);
  useEffect(() => { load(); }, [load]);

  async function toggle(key, on) {
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch('/ui/setup/modules', { method: 'PUT', json: { [key]: on } });
      if (r.status === 403) {
        setErr('Your login is not allowed to switch site modules on. Ask an administrator.');
        return;
      }
      if (!r.ok) throw new Error();
      setMods(await r.json());
      // Switching one on leads straight into setting it up: on its own it does
      // nothing at all, and the next screen is where it becomes real.
      if (on) setOpen(key);
    } catch {
      setErr('Could not change that. Check the connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  const proceed = useCallback(async () => {
    await onDone({
      attendance: !!mods?.attendance?.enabled,
      patrol:     !!mods?.patrol?.enabled,
      pacs:       !!mods?.pacs?.enabled,
      ready: ['attendance', 'patrol', 'pacs'].reduce((n, k) => n + (mods?.[k]?.ready || 0), 0),
    });
    return true;
  }, [onDone, mods]);

  // Inside a module, Continue means "done with this module" and returns to the
  // list rather than leaving the step — returning false holds the wizard here.
  const back = useCallback(async () => { setOpen(null); await load(); return false; }, [load]);

  useEffect(() => {
    if (openKey) {
      const m = SITE_MODULES.find(x => x.key === openKey);
      setNav({ canNext: true, nextLabel: `Done with ${m.title}`, onNext: back });
    } else {
      setNav({ canNext: !busy, onNext: proceed, skip: onSkip, skipLabel: 'None of these' });
    }
  }, [setNav, openKey, busy, proceed, back, onSkip]);

  // ── one module, set up in full ──
  if (openKey) {
    const m = SITE_MODULES.find(x => x.key === openKey);
    const Body = m.Body;
    return (
      <>
        <h1 className="gs-h1">{m.icon} {m.title}</h1>
        <p className="gs-lede">{m.sub}</p>
        <p className="gs-help">
          <button className="gs-btn small ghost" onClick={() => { setOpen(null); load(); }}>
            ← All modules
          </button>
        </p>
        <Body cameras={camOpts} onChanged={load} />
      </>
    );
  }

  // ── the list ──
  return (
    <>
      <h1 className="gs-h1">What else should this site do?</h1>
      <p className="gs-lede">
        Beyond alerting on people, this box can run attendance, guard patrols and badge
        checking. Switch on what this site bought and set it up here — all of it, now,
        so nothing is left half done.
      </p>

      {!mods && !err && (
        <div className="gs-panel">
          <div className="gs-scanning"><span className="gs-spin" /> Loading…</div>
        </div>
      )}

      {mods && SITE_MODULES.map(m => {
        const st   = mods[m.key] || {};
        // A module with no master switch is always "on" — there is nothing to
        // turn on, only something to fill in.
        const on   = m.always || !!st.enabled;
        const conf = st.configured || 0;
        const rdy  = st.ready || 0;
        return (
          <div className="gs-panel" key={m.key} style={{ marginBottom: 18 }}>
            <div className="gs-row" style={{ borderBottom: 'none' }}>
              <span className="gs-row-icon">{m.icon}</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{m.title}</span>
                <span className="gs-row-sub">
                  {!on ? m.sub
                    : conf === 0
                      ? (m.always
                          ? m.sub
                          : `On, but no ${m.what} set up — it will do nothing until you add one`)
                      : rdy === conf
                        ? `${rdy} ${m.what}${rdy === 1 ? '' : 's'}, all working`
                        : `${rdy} of ${conf} ${m.what}s working`}
                </span>
              </span>
              {on && (
                <button className="gs-btn small primary" onClick={() => setOpen(m.key)}>
                  {conf ? 'Set up' : 'Set it up'}
                </button>
              )}
              {!m.always && (
                <button className={`gs-btn small${on ? '' : ' primary'}`} disabled={busy}
                        onClick={() => toggle(m.key, !on)}>
                  {on ? 'Switch off' : 'Switch on'}
                </button>
              )}
            </div>

            {/* A place with no zone drawn is skipped by the engine entirely, so it
                is reported as broken rather than counted as configured. */}
            {on && conf > rdy && m.key === 'access' && (
              <Panel tone="warn" title={`${conf - rdy} of those cameras cannot enforce it`}>
                A list of allowed people needs face recognition on that camera — without
                it there is nothing to compare a face against, and the list does nothing.
              </Panel>
            )}
            {on && conf > rdy && m.key !== 'access' && (
              <Panel tone="warn"
                     title={`${conf - rdy} ${m.what}${conf - rdy === 1 ? '' : 's'} will never fire`}>
                {MODULE_WARN[m.key] || MODULE_WARN._zone}{' '}
                Press <strong>Set up</strong> to fix it.
              </Panel>
            )}
          </div>
        );
      })}

      {mods && !SITE_MODULES.some(m => !m.always && mods[m.key]?.enabled) && (
        <p className="gs-help">
          None of these switched on is a valid answer — the cameras still watch and alert
          on people. Any of them can be switched on here later.
        </p>
      )}

      {err && <div className="gs-err" role="alert">{err}</div>}
    </>
  );
}


// ─── 6. People ────────────────────────────────────────────────────────────────

function PeopleStep({ setNav, onDone, onSkip, industrial }) {
  const [people, setPeople] = useState([]);

  const load = useCallback(() => {
    apiFetch('/faces/known')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setPeople(d.persons || []); })
      .catch(() => {});
  }, []);
  useEffect(load, [load]);

  const proceed = useCallback(async () => { await onDone({ count: people.length }); return true; },
                              [onDone, people.length]);

  useEffect(() => {
    setNav({ canNext: true, onNext: proceed, skip: onSkip, skipLabel: 'Skip for now' });
  }, [setNav, proceed, onSkip]);

  const count = people.length;

  return (
    <>
      <h1 className="gs-h1">{industrial ? 'Who works here?' : 'Who lives here?'}</h1>
      <p className="gs-lede">
        {industrial
          ? 'Add your staff and regular contractors. Parvekshak learns their faces and stops '
            + 'alerting on them — so an alert means somebody who does not belong. These are '
            + 'also the names attendance and patrol check-ins are credited to.'
          : 'Add the people who belong at this property. Parvekshak learns their faces and '
            + 'stops alerting you about them — so the alerts you do get are worth looking at.'}
      </p>

      <Panel tone="warn" title="Worth doing now">
        {industrial
          ? 'Skip this and every worker walking through the gate is an “unknown person”. '
            + 'Attendance and guard patrol both need enrolled faces to credit anyone.'
          : 'Skip this and every family member walking to the gate is an “unknown person”. '
            + 'Most people who turn alerts off do it for this reason.'}
      </Panel>

      {/* Same form as the app's Add to Known page: capture from a camera with the
          raise-a-hand liveness check. Add 2–3 samples per person — pick their
          name again from the list to add another. */}
      <AddKnownPersonForm onEnrolled={load} />

      {count > 0 && (
        <>
          <div className="gs-section-title">Added so far ({count})</div>
          {people.map(p => (
            <div className="gs-row" key={p.name}>
              <span className="gs-row-icon">👤</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{p.name}</span>
                <span className="gs-row-sub">
                  {p.file_count} photo{p.file_count === 1 ? '' : 's'} · won’t raise an alert
                </span>
              </span>
            </div>
          ))}
        </>
      )}

      {industrial && <Watchlist people={people} onChanged={load} />}
    </>
  );
}

// ─── The watchlist ────────────────────────────────────────────────────────────
//
// People who must raise an alarm on sight — a sacked employee, somebody barred
// from site. The opposite of the list above, and an industrial site usually has
// one or two in mind on the day it is commissioned.
//
// It is built from an ENROLLED person rather than a fresh capture, because those
// are the only two ways the box can create a watchlist entry: move an enrolled
// person across, or ban somebody from an alert they already appear in. A capture
// form here would need a third path that does not exist, and inventing one in the
// wizard would mean a watchlist entry with no face embedding — which silently
// matches nobody, the exact failure this feature already had once.
function Watchlist({ people, onChanged }) {
  const [banned, setBanned] = useState([]);
  const [pick,   setPick]   = useState('');
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState(null);

  const load = useCallback(() => apiFetch('/faces/banned')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setBanned(d.persons || []); })
    .catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  async function move() {
    if (!pick) return;
    if (!window.confirm(`Put ${pick} on the watchlist? They will raise an alarm on sight `
                      + 'and will no longer be treated as staff.')) return;
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch(`/faces/known/${encodeURIComponent(pick)}/move_to_banned`,
                               { method: 'POST' });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        setErr(typeof d?.detail === 'string' ? d.detail : 'Could not move that person.');
        return;
      }
      setPick('');
      await load();
      await onChanged();
    } catch {
      setErr('Could not reach the box.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="gs-section-title">Watchlist</div>
      <p className="gs-help">
        Anyone here raises an alarm the moment they are seen, whatever the hour. Add them
        by enrolling them above — with their photo, like anyone else — and then moving
        them across. Somebody who turns up later can be added straight from their alert.
      </p>

      {banned.map(p => (
        <div className="gs-row gs-row-bad" key={p.name}>
          <span className="gs-row-icon">🚫</span>
          <span className="gs-row-text">
            <span className="gs-row-name">{p.name}</span>
            <span className="gs-row-sub">
              {p.file_count} photo{p.file_count === 1 ? '' : 's'} · alarms on sight
            </span>
          </span>
        </div>
      ))}

      {people.length > 0 ? (
        <div className="gs-cred-row" style={{ marginTop: 12 }}>
          <select className="gs-input" value={pick} onChange={e => setPick(e.target.value)}>
            <option value="">Move an enrolled person to the watchlist…</option>
            {people.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
          <button className="gs-btn" onClick={move} disabled={busy || !pick}>
            {busy ? <span className="gs-spin" /> : 'Move'}
          </button>
        </div>
      ) : (
        <p className="gs-help gs-help-after">
          Nobody is enrolled yet — add the person above first, then move them here.
        </p>
      )}

      {err && <div className="gs-err" role="alert">{err}</div>}
    </>
  );
}

// ─── 6. Phone alerts ──────────────────────────────────────────────────────────
//
// The step the original design was missing. Without a registered push token the
// whole system is silent, so this one verifies rather than assumes: it sends a
// real notification and asks the customer to confirm it arrived.

function PhoneStep({ setNav, onDone, industrial }) {
  const [state, setState] = useState('idle');   // idle | asking | sent | confirmed | denied
  const hours = noiseHours(useNoiseWindow());
  const [err,   setErr]   = useState(null);
  const supported = pushSupported();

  async function pair() {
    setErr(null); setState('asking');
    try {
      const ok = await requestPushPermission();
      if (!ok) { setState('denied'); return; }
      const r = await apiFetch('/push/test', { method: 'POST' });
      const d = r.ok ? await r.json().catch(() => ({})) : {};
      if (!r.ok) {
        setErr('Your phone was paired, but the test alert could not be sent.');
        setState('idle');
        return;
      }
      // 200 + "no_tokens" means permission was granted but no token ever
      // reached the box — usually push isn't configured on this box at all.
      // Without this check we would ask "did it arrive?" about an alert that
      // was never sent, and a "yes" would sign off a system that is silent.
      if (d.status === 'no_tokens') { setState('untokened'); return; }
      setState('sent');
    } catch {
      setErr('Something went wrong while pairing this device.');
      setState('idle');
    }
  }

  const proceed = useCallback(async () => {
    await onDone({ confirmed: state === 'confirmed' });
    return true;
  }, [onDone, state]);

  useEffect(() => {
    // 'denied' and 'untokened' are dead ends the customer cannot fix from here,
    // so they may continue — but only after being told plainly that alerts will
    // not reach this phone.
    const settled = ['confirmed', 'denied', 'untokened'].includes(state) || !supported;
    setNav({
      canNext: settled,
      onNext: proceed,
      note: settled ? null : 'Send a test alert and confirm it arrived before continuing',
    });
  }, [setNav, state, proceed, supported]);

  return (
    <>
      <h1 className="gs-h1">Alerts on your phone</h1>
      <p className="gs-lede">
        This is how you find out something is happening. Everything else is optional —
        this is not.
      </p>

      {!supported && (
        <Panel tone="bad" title="This browser can’t receive alerts">
          Open Parvekshak in Chrome or Safari on the phone you want alerts on, and install
          it to your home screen. Then come back to this step.
        </Panel>
      )}

      {state === 'untokened' && (
        <Panel tone="bad" title="This phone could not be registered">
          Your phone allowed notifications, but it never reached this box — push
          notifications are not set up on it yet. Ask your installer to finish that,
          then come back here. Until then you will only see alerts when the app is open.
        </Panel>
      )}

      {supported && !['sent', 'confirmed', 'untokened'].includes(state) && (
        <>
          <Panel title="What happens next">
            Your phone will ask permission to show notifications. Say yes, and we’ll send a
            test alert straight away so you can check it actually arrives.
            {/* The test always comes through (it is sent like an SOS); a real alert
                rings only in the noise window — say so, or a quiet night alert later
                looks like a broken phone (PRVSK-143). Industrial sets hours later. */}
            {!industrial && hours && hours !== 'at any hour' && (
              <> The test always comes through. Real alerts ring your phone {hours}; outside
              those hours they arrive quietly, and you can change that under Siren hours
              on the Home screen.</>
            )}
          </Panel>
          <button className="gs-btn primary" onClick={pair} disabled={state === 'asking'}>
            {state === 'asking' ? <><span className="gs-spin" /> Pairing…</> : 'Pair this phone and send a test'}
          </button>
        </>
      )}

      {state === 'denied' && (
        <Panel tone="bad" title="Notifications are blocked">
          Your phone refused permission, so alerts can’t reach it. You can turn them on in
          your browser’s site settings and come back here. Until then, alerts will only
          appear when you open the app.
        </Panel>
      )}

      {state === 'sent' && (
        <Panel tone="warn" title="Did a test alert just arrive on this phone?">
          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <button className="gs-btn primary" onClick={() => setState('confirmed')}>Yes, it arrived</button>
            <button className="gs-btn" onClick={pair}>No — try again</button>
          </div>
        </Panel>
      )}

      {state === 'confirmed' && (
        <Panel tone="good" title="This phone is set up">
          Alerts will reach it. You can add another phone later from this screen — a second
          family member, for instance.
        </Panel>
      )}

      {err && <div className="gs-err">{err}</div>}
    </>
  );
}

// ─── 7. When to protect ───────────────────────────────────────────────────────

// Two separate things, because they genuinely are:
//
//   WATCHING  defaults to around the clock. The customer is usually out, or asleep,
//             and a break-in at two in the afternoon should still be seen and
//             recorded. Nothing is gained by being blind for half the day.
//
//   NOISE     defaults to night. A siren going off while the family is home and
//             awake is a nuisance, and the phone is part of the noise — it wakes
//             the house exactly as a siren does.
//
// So the normal home ends up watching 24/7, with the siren, the lights and the
// phone all limited to 23:00-06:00. That is the shape a customer actually wants
// and it is nobody's default anywhere else in this product.
function ScheduleStep({ setNav, onDone }) {
  const [mode,   setMode]   = useState('ALWAYS');
  const [start,  setStart]  = useState('22:00');
  const [end,    setEnd]    = useState('06:00');
  const [dStart, setDStart] = useState('23:00');
  const [dEnd,   setDEnd]   = useState('06:00');
  const [err,    setErr]    = useState(null);

  useEffect(() => {
    apiFetch('/settings/alert-schedule')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d) return;
        setMode(d.mode || 'ALWAYS');
        // Only when the stored mode actually USES them. Behind mode ALWAYS the
        // start/end are vestigial — a live site holds 06:00 / 05:59, a 24h sentinel
        // — and adopting those meant picking "Night only" silently proposed watching
        // from six in the morning until one minute to six. Leave the night defaults
        // in place instead, so switching offers real night hours.
        if (d.mode === 'TIME_BASED') {
          if (d.start) setStart(d.start);
          if (d.end)   setEnd(d.end);
        }
      })
      .catch(() => {});
    // The noise window is carried on PUSH_SCHEDULE, which the setup endpoint keeps
    // in step with the device window — so this reads back whatever is already set
    // without this screen needing to know about relays.
    apiFetch('/settings/push-schedule')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d || d.mode === '24x7') return;
        if (d.start) setDStart(d.start);
        if (d.end)   setDEnd(d.end);
      })
      .catch(() => {});
  }, []);

  // One window, applied to all three things it governs: whether an alert is raised,
  // whether the phone buzzes, and whether the siren fires.
  //
  // This used to PUT /settings/alert-schedule, which sets only the first. Push has
  // its own schedule defaulting to 00:30-05:00, so a customer choosing 22:00-06:00
  // got alerts from ten at night and a silent phone until half past midnight — two
  // and a half hours where the system believed it was protecting them and said
  // nothing, with no sign of it anywhere in the UI.
  // A cleared <input type="time"> gives '' — saving that crashed the camera
  // detection workers (PRVSK-108 #1). The box refuses it too; this stops it here.
  const okTime = t => /^([01]\d|2[0-3]):[0-5]\d$/.test(t || '');
  const timesOk = (mode !== 'TIME_BASED' || (okTime(start) && okTime(end))) && okTime(dStart) && okTime(dEnd);

  const save = useCallback(async () => {
    setErr(null);
    if (!timesOk) { setErr('Please fill in every time.'); return false; }
    const r = await apiFetch('/ui/setup/protection-window', {
      method: 'PUT',
      json: { mode, start, end, device_start: dStart, device_end: dEnd },
    });
    if (!r.ok) { setErr('Could not save the schedule.'); return false; }
    await onDone({ mode, start, end, device_start: dStart, device_end: dEnd });
    return true;
  }, [mode, start, end, dStart, dEnd, onDone, timesOk]);

  useEffect(() => { setNav({ canNext: timesOk, onNext: save }); }, [setNav, save, timesOk]);

  return (
    <>
      <h1 className="gs-h1">When should the cameras watch?</h1>
      <p className="gs-lede">
        Two separate things, and most people want them set differently: the hours the
        cameras are watching, and the hours the siren and lights are allowed to go
        off and drive someone away.
      </p>

      <Field label="Watching hours">
        <div className="gs-choices">
          {/* "24 hours a day", never "All day" — against "Night only" that reads as
              DAYTIME ONLY, the opposite of what it sets. */}
          <Choice
            icon="🛡️" title="24 hours a day" sub="Recommended — nothing is missed"
            selected={mode === 'ALWAYS'} onClick={() => setMode('ALWAYS')}
          />
          <Choice
            icon="🌙" title="Night only"
            sub="Alerts at night only — cameras keep watching all day"
            selected={mode === 'TIME_BASED'} onClick={() => setMode('TIME_BASED')}
          />
        </div>
      </Field>

      {mode === 'TIME_BASED' && (
        <div className="gs-input-row" style={{ maxWidth: 520 }}>
          <Field label="From" help="Cameras start watching.">
            <input className="gs-input" type="time" value={start} onChange={e => setStart(e.target.value)} />
          </Field>
          <Field label="To" help="Cameras stop watching.">
            <input className="gs-input" type="time" value={end} onChange={e => setEnd(e.target.value)} />
          </Field>
        </div>
      )}

      {/* A Field, like "Watching hours" above it. These are sibling questions and
          were rendered with different components — one a bold Field label, the other
          a muted uppercase section title — so the second read as a lesser heading
          when it is the more important of the two. */}
      <Field
        label="When should it scare them off?"
        help={'A siren and floodlights are what actually stop a break-in — most people '
            + 'turn and run the moment they go off, often before anyone in the house has '
            + 'woken up. Your phone is alerted at the same time. Outside these hours the '
            + 'cameras still watch; nothing makes a sound.'}
      >
        <div className="gs-input-row" style={{ maxWidth: 520 }}>
          <Field label="From" help="Siren and lights armed from here.">
            <input className="gs-input" type="time" value={dStart}
                   onChange={e => setDStart(e.target.value)} />
          </Field>
          <Field label="To" help="They stand down again.">
            <input className="gs-input" type="time" value={dEnd}
                   onChange={e => setDEnd(e.target.value)} />
          </Field>
        </div>
      </Field>

      {mode === 'ALWAYS' && (
        <Panel title="What this means during the day">
          Someone the system doesn’t recognise is still spotted, recorded and waiting
          for you under Alerts — nothing sounds and your phone stays quiet until
          {dStart}.{productHas('known-faces-add')
            ? ' Add your household on the People step so the people who live here are recognised.'
            : ''}
        </Panel>
      )}

      <div className="gs-section-title">When you’re home</div>
      <Panel title="Pausing alerts">
        You don’t have to change this schedule when you’re up late or have guests. There
        is a pause button on your dashboard that silences alert devices for a while, and
        turns itself back on afterwards.
      </Panel>

      {!timesOk && <div className="gs-err">Please fill in every time to continue.</div>}
      {err && timesOk && <div className="gs-err">{err}</div>}
    </>
  );
}

// ─── 8. Alert devices ─────────────────────────────────────────────────────────
//
// The customer picks what a device DOES; the wizard works out how it connects.
// Wired relay boards and Wi-Fi smart plugs/bulbs are discovered together and
// shown in one list — "board vs Wi-Fi" is not a question a homeowner should be
// asked.

const DEVICE_KINDS = [
  { type: 'light',   icon: '💡', title: 'Light',      sub: 'Floodlight, bulb or lamp — lights up on an alert' },
  { type: 'siren',   icon: '🚨', title: 'Siren',      sub: 'Makes a loud noise' },
  { type: 'speaker', icon: '🔊', title: 'Speaker',    sub: 'Plays a spoken warning' },
  { type: 'onoff',   icon: '🔌', title: 'Other',      sub: 'Any other switched device' },
];

// How a light behaves — one choice instead of a second schedule. "On at night"
// follows the alert hours from the Hours step (the box keeps it in step if they
// change later). Flashing needs a light the box can switch fast: a WiZ bulb or a
// wired relay. A smart plug's mechanical switch would wear out, so a plug only
// gets the steady modes.
function lightModes(canBlink, secs) {
  return [
    { id: 'steady', icon: '💡', title: 'Turn on during an alert',
      sub: `Lights up for ${secs} seconds when someone is spotted, then switches off.` },
    canBlink && { id: 'blink', icon: '✨', title: 'Flash during an alert',
      sub: `Flashes for ${secs} seconds when someone is spotted, then switches off. Hard to miss.` },
    { id: 'night', icon: '🌙', title: canBlink ? 'On at night, flash on an alert' : 'On at night',
      sub: canBlink
        ? 'Stays on during your protection hours and flashes when someone is spotted. Goes back to on afterwards.'
        : 'Stays on during your protection hours, and stays on during an alert.',
      night: true },
  ].filter(Boolean);
}

function LightModePicker({ value, onChange, canBlink, secs, hours }) {
  const [open, setOpen] = useState(false);
  const modes   = lightModes(canBlink, secs);
  const hoursOk = hours?.mode === 'TIME_BASED' && hours.start && hours.end;
  const cur     = modes.find(m => m.id === value) || modes[0];
  return (
    <div className={`gs-dd${open ? ' open' : ''}`}>
      <button type="button" className="gs-dd-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>{cur.icon} {cur.title}</span><span className="gs-dd-caret">{open ? '⌃' : '⌄'}</span>
      </button>
      {open && modes.map(m => {
        const off = m.night && !hoursOk;
        return (
          <button type="button" key={m.id} disabled={off}
                  className={`gs-dd-opt${m.id === value ? ' sel' : ''}`}
                  onClick={() => { onChange(m.id); setOpen(false); }}>
            <span className="gs-dd-ico">{m.icon}</span>
            <span className="gs-dd-text">
              <span className="gs-dd-title">{m.title}</span>
              <span className="gs-dd-sub">{m.sub}</span>
              {m.night && (hoursOk
                ? <span className="gs-dd-win">🕙 {hours.start} – {hours.end} · your protection hours</span>
                : <span className="gs-dd-sub">Set protection hours (e.g. 22:00 – 06:00) in the Hours step to use this.</span>)}
            </span>
            {m.id === value && <span className="gs-dd-chk">✓</span>}
          </button>
        );
      })}
      {!canBlink && (
        <p className="gs-help gs-dd-note">
          Smart plugs can’t flash — their switch would wear out. For a flashing light use a WiZ
          bulb or a wired light.
        </p>
      )}
    </div>
  );
}

function DevicesStep({ setNav, onDone, onSkip, industrial }) {
  const noiseHrs = noiseHours(useNoiseWindow());
  const [status,  setStatus]  = useState(null);
  const [wiz,     setWiz]     = useState(null);     // discovered WiZ devices
  const [finding, setFinding] = useState(false);
  const [pick,    setPick]    = useState(null);     // discovered device being added
  const [kind,    setKind]    = useState('light');
  const [name,    setName]    = useState('');
  const [testing, setTesting] = useState(null);
  const [results, setResults] = useState({});     // mac → { ok, msg } of its last Test
  const [err,     setErr]     = useState(null);
  const [adding,  setAdding]  = useState(false);     // one add at a time — a double press sent two
  const [lightMode, setLightMode] = useState('steady');
  const [hours,   setHours]   = useState(null);     // global alert hours, for "On at night"

  useEffect(() => {
    apiFetch('/settings/alert-schedule').then(r => (r.ok ? r.json() : null)).then(setHours).catch(() => {});
  }, []);

  // pick is either a discovered WiZ device ({mac, ip, kind}) or an already set-up
  // channel being changed ({existing: ch}).
  const startPick = (p) => {
    setPick(p); setErr(null);
    const ch = p.existing;
    setName(ch ? ch.name || '' : '');
    setKind(ch ? (ch.type || 'onoff') : 'light');
    setLightMode(ch?.fixed_follows_alert_hours ? 'night' : ch?.alert_mode === 'blink' ? 'blink' : 'steady');
  };
  const pickIsPlug = p => (p?.existing ? p.existing.backend === 'wiz' && p.existing.wiz_kind !== 'bulb'
                                       : p?.kind === 'plug');
  const canBlink   = pick ? !pickIsPlug(pick) : false;
  const pickSecs   = pick?.existing?.auto_off_seconds || 15;
  const lightBody  = () => (kind !== 'light' ? {} : {
    alert_mode: lightMode === 'steady' || !canBlink ? 'steady' : 'blink',
    fixed_follows_alert_hours: lightMode === 'night',
  });

  const load = useCallback(() => {
    apiFetch('/ui/devices/status')
      .then(r => (r.ok ? r.json() : null))
      .then(d => d && setStatus(d))
      .catch(() => {});
  }, []);
  useEffect(load, [load]);

  async function find() {
    setFinding(true); setErr(null);
    try {
      await apiFetch('/ui/devices/relay/scan', { method: 'POST' }).catch(() => {});
      const r = await apiFetch('/ui/devices/wiz/discover');
      const d = r.ok ? await r.json() : { devices: [] };
      setWiz((d.devices || []).filter(x => !x.channel));
      load();
    } catch {
      setErr('The search could not run.');
    } finally {
      setFinding(false);
    }
  }

  // The result is shown on the device's own row (green / red), not in one shared
  // message at the bottom — with several devices the customer must see which
  // one worked.
  async function test(dev) {
    setTesting(dev.mac); setErr(null);
    setResults(m => ({ ...m, [dev.mac]: null }));
    let res;
    try {
      const r = await apiFetch('/ui/devices/wiz/test', { method: 'POST', json: { mac: dev.mac, ip: dev.ip } });
      if (r.ok) res = { ok: true, msg: 'Worked — it switched on for 2 seconds.' };
      else {
        const d = await r.json().catch(() => ({}));
        res = { ok: false, msg: d.detail || 'That device did not respond.' };
      }
    } catch {
      res = { ok: false, msg: 'Could not reach this box.' };
    }
    setResults(m => ({ ...m, [dev.mac]: res }));
    setTesting(null);
  }

  const testLine = mac => {
    const r = results[mac];
    if (!r) return null;
    return (
      <span className={`gs-row-sub ${r.ok ? 'gs-test-ok' : 'gs-test-bad'}`}>
        {r.ok ? '✓ ' : '✗ '}{r.msg}
      </span>
    );
  };
  const testCls = mac => (results[mac] ? (results[mac].ok ? ' gs-row-ok' : ' gs-row-bad') : '');

  async function add() {
    if (!pick || adding) return;
    setErr(null);
    setAdding(true);
    try {
      await addPicked();
    } catch {
      setErr('Could not reach the box. Check the connection and try again.');
    } finally {
      setAdding(false);
    }
  }

  async function addPicked() {
    const finalName = name.trim() || DEVICE_KINDS.find(k => k.type === kind).title;
    let r;
    if (pick.existing) {
      const ch = pick.existing;
      const light = lightBody();
      // Leaving "On at night" drops the window it was following; a schedule set by
      // hand elsewhere is left alone.
      if (kind !== 'light' || !light.fixed_follows_alert_hours) {
        delete light.fixed_follows_alert_hours;
        if (ch.fixed_follows_alert_hours) Object.assign(light, { fixed_schedule: null, fixed_follows_alert_hours: false });
      }
      r = await apiFetch(`/ui/devices/relay/channels/${ch.channel}`, {
        method: 'PATCH', json: { name: finalName, type: kind, ...light },
      });
    } else {
      r = await apiFetch('/ui/devices/wiz/channels', {
        method: 'POST',
        json: {
          name: finalName,
          mac: pick.mac, ip: pick.ip, kind: pick.kind, device_type: kind,
          on_alert: true, alert_schedule: 'always', ...lightBody(),
        },
      });
    }
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setErr(d.detail || 'Could not add that device.');
      return;
    }
    setPick(null); setName('');
    if (!pick.existing) setWiz(w => (w || []).filter(x => x.mac !== pick.mac));
    load();
  }

  const channels = status?.relay_channels || [];

  const proceed = useCallback(async () => { await onDone({ count: channels.length }); return true; },
                              [onDone, channels.length]);

  useEffect(() => {
    // "I don't have any" only when there are none: pressed with a siren already
    // listed, it marked the step skipped and the Done screen then listed Alert
    // devices under "You skipped these" (PRVSK-115).
    setNav(channels.length
      ? { canNext: true, onNext: proceed }
      : { canNext: true, onNext: proceed, skip: onSkip, skipLabel: 'I don’t have any' });
  }, [setNav, proceed, onSkip, channels.length]);

  return (
    <>
      <h1 className="gs-h1">What should happen on an alert?</h1>
      <p className="gs-lede">
        {/* Only the industrial flow has an Hours step before this one (PRVSK-143). */}
        {industrial
          ? 'Your phone is alerted in the same hours as the siren, which you set on the previous step. '
          : noiseHrs
            ? `Your phone and any siren or light sound ${noiseHrs} — change that any time under Siren hours on the Home screen. `
            : 'Your phone and any siren or light sound in the same hours — set them under Siren hours on the Home screen. '}
        If you have a floodlight, siren or speaker — including a smart
        plug anything is plugged into — we can switch it on too.
      </p>

      {channels.length > 0 && (
        <>
          <div className="gs-section-title">Already set up</div>
          {channels.map(ch => {
            const live = status?.wiz_status?.[String(ch.channel)];
            return (
              <div className={`gs-row${ch.backend === 'wiz' ? testCls(ch.wiz_mac) : ''}`} key={ch.channel}>
                <span className="gs-row-icon">
                  {DEVICE_KINDS.find(k => k.type === (ch.type || 'onoff'))?.icon || '🔌'}
                </span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{ch.name}</span>
                  <span className="gs-row-sub">
                    {ch.backend === 'wiz'
                      ? `Smart ${ch.wiz_kind === 'bulb' ? 'bulb' : 'plug'} · ${live?.reachable === false ? 'not reachable' : 'reachable'}`
                      : 'Wired'}
                  </span>
                  {ch.backend === 'wiz' && testLine(ch.wiz_mac)}
                </span>
                {ch.backend === 'wiz' && (
                  <button className="gs-btn small" disabled={testing === ch.wiz_mac}
                          onClick={() => test({ mac: ch.wiz_mac, ip: ch.wiz_ip })}>
                    {testing === ch.wiz_mac ? 'Testing…' : 'Test'}
                  </button>
                )}
                {/* Wired devices are added by the search with no questions asked —
                    this is where they get a type, a name and (lights) a mode. */}
                {!pick && (
                  <button className="gs-btn small" onClick={() => startPick({ existing: ch })}>Set up</button>
                )}
              </div>
            );
          })}
        </>
      )}

      <div className="gs-section-title">{pick?.existing ? `Set up “${pick.existing.name}”` : 'Add a device'}</div>

      {!pick && (
        <>
          <Panel title="Before you search">
            A smart plug or bulb must already be set up in its own app, on the same Wi-Fi
            as this box. For a WiZ plug or bulb, open the WiZ app → Settings → Security,
            keep <b>Allow local</b> on and choose <b>Allow all controls</b> — without it
            the device is found but can’t be switched. Wired devices are found automatically.
          </Panel>
          <button className="gs-btn" onClick={find} disabled={finding}>
            {finding ? <><span className="gs-spin" /> Looking…</> : 'Find my devices'}
          </button>
        </>
      )}

      {wiz !== null && wiz.length === 0 && !pick && !finding && (
        <div className="gs-empty" style={{ marginTop: 16 }}>
          Nothing new found. Check the device is switched on and on the same Wi-Fi as this
          box. For WiZ: WiZ app → Settings → Security → Allow local on, Allow all controls.
        </div>
      )}

      {!pick && wiz?.map(d => (
        <div className={`gs-row${testCls(d.mac)}`} key={d.mac}>
          <span className="gs-row-icon">{d.kind === 'bulb' ? '💡' : '🔌'}</span>
          <span className="gs-row-text">
            <span className="gs-row-name">Smart {d.kind === 'bulb' ? 'bulb' : 'plug'}</span>
            <span className="gs-row-sub">{d.ip}</span>
            {testLine(d.mac)}
          </span>
          <button className="gs-btn small" onClick={() => test(d)} disabled={testing === d.mac}>
            {testing === d.mac ? 'Testing…' : 'Flash it'}
          </button>
          <button className="gs-btn small primary" onClick={() => startPick(d)}>Use this</button>
        </div>
      ))}

      {pick && (
        <>
          <Field
            label="What is plugged into it?"
            help="This decides what the device does on an alert, and when."
          >
            <div className="gs-choices">
              {DEVICE_KINDS.map(k => (
                <Choice key={k.type} icon={k.icon} title={k.title} sub={k.sub}
                        selected={kind === k.type} onClick={() => setKind(k.type)} />
              ))}
            </div>
          </Field>

          <Field label="Name it" help="So you recognise it on your dashboard.">
            <input className="gs-input" value={name} onChange={e => setName(e.target.value)}
                   placeholder={DEVICE_KINDS.find(k => k.type === kind).title} maxLength={40} />
          </Field>

          {kind === 'light' && (
            <Field label="How should the light work?"
                   help="Uses the hours you set earlier — no separate schedule to manage.">
              <LightModePicker value={lightMode} onChange={setLightMode} canBlink={canBlink}
                               secs={pickSecs} hours={hours} />
            </Field>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="gs-btn primary" onClick={add} disabled={adding}>
              {adding ? <span className="gs-spin" /> : (pick.existing ? 'Save' : 'Add this device')}
            </button>
            <button className="gs-btn ghost" onClick={() => setPick(null)} disabled={adding}>Cancel</button>
          </div>
        </>
      )}

      {err && <div className="gs-err">{err}</div>}
    </>
  );
}

// ─── 9. Live test ─────────────────────────────────────────────────────────────
//
// Arms a short window in which the night schedule is bypassed, so the customer
// can prove detection works at whatever hour they happen to be setting up. Only
// the clock is bypassed — every detection gate still runs, so a pass here is a
// pass at 2am.

function TestStep({ setNav, onDone, onSkip, cameras }) {
  const [armed,   setArmed]   = useState(false);
  const [left,    setLeft]    = useState(0);
  const [hits,    setHits]    = useState([]);
  const [done,    setDone]    = useState(false);
  const [err,     setErr]     = useState(null);
  const sinceRef = useRef(0);
  const pollRef  = useRef(null);

  const stop = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = null;
    apiFetch('/ui/setup/test-detection', { method: 'DELETE' }).catch(() => {});
  }, []);

  useEffect(() => () => stop(), [stop]);

  async function start() {
    setErr(null); setHits([]); setDone(false);
    const r = await apiFetch('/ui/setup/test-detection', { method: 'POST', json: { seconds: 120 } });
    if (!r.ok) { setErr('Could not start the test.'); return; }
    const d = await r.json();
    sinceRef.current = d.started_at;
    setArmed(true); setLeft(d.seconds);
    pollRef.current = setInterval(async () => {
      const s = await apiFetch(`/ui/setup/test-detection?since=${sinceRef.current}`);
      if (!s.ok) return;
      const j = await s.json();
      setLeft(j.seconds_left);
      if (j.alerts?.length) setHits(j.alerts);
      if (j.detected || !j.armed) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setArmed(false);
        setDone(true);
      }
    }, 2000);
  }

  const proceed = useCallback(async () => {
    stop();
    await onDone({ detected: hits.length > 0 });
    return true;
  }, [onDone, hits.length, stop]);

  useEffect(() => {
    setNav({
      canNext: !armed,
      onNext: proceed,
      skip: armed ? null : onSkip,
      skipLabel: 'Skip the test',
      note: armed ? 'Test running — walk into view, or wait for it to finish' : null,
    });
  }, [setNav, armed, proceed, onSkip]);

  return (
    <>
      <h1 className="gs-h1">Let’s check it works</h1>
      <p className="gs-lede">
        Ask someone to walk into one of your protected areas. We’ll watch for two
        minutes and tell you exactly what the system saw.
      </p>

      {!armed && !done && (
        <>
          <Panel title="This is a real test">
            For the next two minutes we ignore your night-only schedule so the test works
            at any hour. Nothing else is relaxed — if it detects someone now, it would
            have detected them at 2am.
          </Panel>
          <button className="gs-btn primary" onClick={start} disabled={!cameras.length}>
            Start the test
          </button>
        </>
      )}

      {armed && (
        <Panel tone="warn" title={`Watching… ${left}s left`}>
          <div className="gs-scanning"><span className="gs-spin" /> Walk into view of a camera now.</div>
        </Panel>
      )}

      {done && hits.length > 0 && (
        <Panel tone="good" title="Detected — your system is working">
          {hits.map(h => (
            <div key={h.id} style={{ marginTop: 6 }}>
              {h.label} on <strong>{h.cam_name}</strong> at{' '}
              {new Date((h.ts || 0) * 1000).toLocaleTimeString()}
            </div>
          ))}
        </Panel>
      )}

      {done && hits.length === 0 && (
        <Panel tone="bad" title="Nothing was detected">
          That usually means one of these:
          <ul style={{ margin: '10px 0 0 18px', lineHeight: 1.7 }}>
            <li>Nobody actually walked into view during the two minutes</li>
            <li>They walked through an area you marked as ignored</li>
            <li>They were recognised as someone who lives here</li>
            <li>The camera can’t see that spot well enough — lighting or angle</li>
          </ul>
          <div style={{ marginTop: 14 }}>
            <button className="gs-btn" onClick={start}>Try the test again</button>
          </div>
        </Panel>
      )}

      {err && <div className="gs-err">{err}</div>}
    </>
  );
}

// ─── 10. Ready ────────────────────────────────────────────────────────────────

// Questions a customer is allowed to be unsure about, and what each one is for.
// Shown on the last screen when skipped, so "you can come back to it" is something
// the product actually does rather than something it says once and forgets.
const SKIPPABLE_LABELS = {
  linger:  ['How people behave', 'Stops someone who stands still being mistaken for scenery'],
  animals: ['Pets & animals', 'Stops a cat or dog setting off the alarm at night'],
  modules: ['Site modules',  'Attendance, guard patrol and badge checking — off until switched on'],
  people:  ['People',         'Lets the system recognise your household and stay quiet for them'],
  devices: ['Alert devices',  'Turns on a light or siren when something is spotted'],
  test:    ['Live test',      'Confirms the system spots a person walking past'],
};

function ReadyStep({ setNav, cameras, state, onFinish, goToStep, industrial }) {
  const navigate = useNavigate();
  useEffect(() => {
    setNav({
      canNext: true,
      nextLabel: 'Go to my dashboard',
      onNext: async () => { await onFinish(); navigate('/'); return true; },
    });
  }, [setNav, onFinish, navigate]);

  const armed  = cameras.filter(c => (c.detect_mode || 'full') !== 'off').length;
  const zones  = cameras.reduce((n, c) => n + (c.cam_config?.exclusion_zones?.length || 0), 0);
  const sched  = state?.steps?.schedule?.detail;
  const people = state?.steps?.people?.detail?.count || 0;
  const devs   = state?.steps?.devices?.detail?.count || 0;
  const knownOn = productHas('known-faces-add');   // product may leave known people out
  // Promise a siren and lights only when an alert device was set up (PRVSK-115).
  const noise = devs > 0 ? 'sets off your alert devices and alerts your phone' : 'alerts your phone';
  // The home flow sets no hours: describe the box's own noise window, not the
  // 22:00/23:00 fallbacks below, which were never set (PRVSK-143).
  const nw = useNoiseWindow();
  const homeHours = noiseHours(nw);
  return (
    <>
      <h1 className="gs-h1">You’re protected</h1>
      <p className="gs-lede">
        {!sched
          ? (homeHours
              ? `Your cameras watch around the clock. ${homeHours === 'at any hour' ? 'At any hour' : homeHours.charAt(0).toUpperCase() + homeHours.slice(1)}, `
                + `anyone they don’t recognise ${noise}. Outside those hours it is all recorded and `
                + 'waiting under Alerts. Change the hours any time under Siren hours on the Home screen.'
              : nw
                ? 'Your cameras watch around the clock, and everything is recorded under Alerts. '
                  + 'Alert sounds are off — turn them on under Siren hours on the Home screen.'
                : 'Your cameras watch around the clock, and everything is recorded under Alerts.')
        : sched?.mode === 'ALWAYS'
          ? `Your cameras watch around the clock. Between ${sched?.device_start || '23:00'} and `
            + `${sched?.device_end || '06:00'}, anyone they don’t recognise ${noise}. `
            + 'Outside those hours it is all recorded and waiting under Alerts.'
          : `Your cameras watch between ${sched?.start || '22:00'} and ${sched?.end || '06:00'}. `
            + `Between ${sched?.device_start || '23:00'} and ${sched?.device_end || '06:00'}, anyone they `
            + `don’t recognise ${noise}.`}
      </p>
      {sched?.mode === 'TIME_BASED' && (
        // Seen in testing: a daytime walk-past after setup gave no alert and looked
        // like a detection failure. Say so here (PRVSK-115).
        <Panel title="During the day">
          Nobody is alerted outside {sched?.start || '22:00'}–{sched?.end || '06:00'}. Cameras
          keep watching. When nobody is home, choose <b>I’m out</b> under Siren hours on the
          Home screen and every camera watches round the clock.
        </Panel>
      )}

      <div className="gs-tiles">
        <div className="gs-tile"><div className="gs-tile-num">{armed}</div><div className="gs-tile-label">Cameras watching</div></div>
        {knownOn && <div className="gs-tile"><div className="gs-tile-num">{people}</div><div className="gs-tile-label">{industrial ? 'Staff known' : 'People known'}</div></div>}
        {/* The two industrial steps, reported in the same terms as everything
            else: a count the customer can check against what they just did. */}
        {industrial && (
          <div className="gs-tile">
            <div className="gs-tile-num">{state?.steps?.models?.detail?.placements || 0}</div>
            <div className="gs-tile-label">AI models placed</div>
          </div>
        )}
        {industrial && (() => {
          const md = state?.steps?.modules?.detail || {};
          const on = ['attendance', 'patrol', 'pacs'].filter(k => md[k]).length;
          return (
            <div className="gs-tile">
              <div className="gs-tile-num">{on}</div>
              <div className="gs-tile-label">Site modules on</div>
            </div>
          );
        })()}
        <div className="gs-tile"><div className="gs-tile-num">{devs}</div><div className="gs-tile-label">Alert devices</div></div>
        <div className="gs-tile"><div className="gs-tile-num">{zones}</div><div className="gs-tile-label">Areas ignored</div></div>
      </div>

      {(() => {
        const skipped = Object.keys(SKIPPABLE_LABELS)
          .filter(k => state?.steps?.[k]?.skipped && !state?.steps?.[k]?.done);
        if (!skipped.length) return null;
        return (
          <>
            <div className="gs-section-title">You skipped these — finish them any time</div>
            <p className="gs-help">
              Nothing here is required, and your system is already protecting you. Each one
              just makes it quieter or more useful.
            </p>
            {skipped.map(k => (
              <div className="gs-row" key={k}>
                <span className="gs-row-icon">↩️</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{SKIPPABLE_LABELS[k][0]}</span>
                  <span className="gs-row-sub">{SKIPPABLE_LABELS[k][1]}</span>
                </span>
                <button className="gs-btn small" onClick={() => goToStep(k)}>Do it now</button>
              </div>
            ))}
          </>
        );
      })()}

      <div className="gs-section-title">What happens now</div>
      <Panel title="When something is spotted">
        Your phone buzzes with a photo of whoever it saw. Tap it to see the video. Everything
        is also waiting for you under <strong>Alerts</strong>.
      </Panel>
      <Panel title="If you get an alert you didn’t want">
        Come back to Guided Setup and mark that part of the view as ignored
        {knownOn ? ', or add the person so they’re recognised next time' : ''}. That’s almost
        always the right fix.
      </Panel>
      <p className="gs-help">
        You can reopen Guided Setup from the menu whenever you want to change any of this.
      </p>
    </>
  );
}

// ─── Shell ────────────────────────────────────────────────────────────────────

export default function GuidedSetupPage() {
  const navigate = useNavigate();
  // The current step is held by its ID, not its position (PRVSK-151). The list
  // below changes once entitlements arrive — home until then, industrial after —
  // and a position kept across that change pointed at a different step (or past
  // the end of a shorter list, which crashed the page).
  const [curId,   setCurId]   = useState(null);
  const [state,   setState]   = useState(null);
  const [cameras, setCameras] = useState([]);
  const [nav,     setNav]     = useState({});
  const [busy,    setBusy]    = useState(false);

  // Steps the product leaves out (product_profiles on the box): without the
  // Add to Known feature there is no "Who lives here?" step. Subscribing keeps
  // the list right once entitlements arrive after first render — which is also
  // what gets the industrial list picked up, since clientType arrives with them.
  const { productHidden, clientType, menus } = useEntitlements();   // menus null = not hydrated yet
  const industrial = clientType === 'industrial';
  const steps = useMemo(
    () => (industrial ? INDUSTRIAL_STEPS : STEPS)
      .filter(s => s.id !== 'people' || productHas('known-faces-add')),
    [industrial, productHidden]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Position of the current step in TODAY's list. A step that has left the list
  // keeps the place it had (clamped), never an index past the end.
  const lastI = useRef(0);
  const found = curId == null ? 0 : steps.findIndex(s => s.id === curId);
  const i = found >= 0 ? found : Math.min(lastI.current, steps.length - 1);
  lastI.current = i;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  // setI(n) / setI(n => …) by position, as everything below uses it — stored as
  // the step's id, so it survives the list changing.
  const setI = useCallback(next => {
    setCurId(prevId => {
      const list = stepsRef.current;
      const cur  = Math.max(0, list.findIndex(s => s.id === prevId));
      const n    = typeof next === 'function' ? next(cur) : next;
      const c    = Math.max(0, Math.min(n, list.length - 1));
      return list[c] ? list[c].id : prevId;
    });
  }, []);

  const step = steps[i];

  const reloadCameras = useCallback(async () => {
    const r = await apiFetch('/ui/cameras');
    if (r.ok) { const d = await r.json(); setCameras(d.cameras || []); }
  }, []);

  const reloadState = useCallback(async () => {
    const r = await apiFetch('/ui/setup/state');
    if (r.ok) setState(await r.json());
  }, []);

  useEffect(() => { reloadCameras(); reloadState(); }, [reloadCameras, reloadState]);

  // Resume where the customer left off, rather than at the welcome screen — but
  // only once entitlements are in, so it searches the RIGHT list (PRVSK-151).
  // Before, it ran against the home list an industrial site shows for a moment
  // after login, missed "models", and settled on Welcome. If entitlements never
  // answer, resume after a few seconds with what is there.
  const resumed = useRef(false);
  const [entWaitOver, setEntWaitOver] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setEntWaitOver(true), 4000);
    return () => clearTimeout(t);
  }, []);
  const entReady = menus != null || entWaitOver;
  useEffect(() => {
    if (resumed.current || !state || !entReady) return;
    resumed.current = true;
    const first = steps.findIndex(s => s.key && (state.outstanding || []).includes(s.key));
    if (first > 0) setI(first);
  }, [state, steps, entReady, setI]);

  // Stable setter — step components call this from an effect, so an unstable
  // identity here would loop.
  const setNavStable = useCallback(cfg => {
    setNav(prev => {
      const next = { canNext: true, ...cfg };
      const same = ['canNext', 'nextLabel', 'note', 'skipLabel'].every(k => prev[k] === next[k])
        && prev.onNext === next.onNext && prev.skip === next.skip;
      return same ? prev : next;
    });
  }, []);

  // Saves a step's progress, and THROWS when the box didn't keep it (PRVSK-149).
  // A failure used to be ignored: Continue moved on, the step was never recorded,
  // and the wizard came back later on a step the customer had already done.
  const mark = useCallback(async (key, detail, skipped = false) => {
    if (!key) return;
    let r;
    try {
      r = await apiFetch('/ui/setup/state', {
        method: 'PUT', json: { step: key, done: !skipped, skipped, detail: detail || null },
      });
    } catch {
      throw new Error('Could not reach the box, so this step was not saved. Check the connection and try again.');
    }
    if (!r.ok) throw new Error('The box did not save this step. Try again.');
    setState(await r.json());
  }, []);

  // What went wrong saving the step, shown under it; cleared on any move.
  const [saveErr, setSaveErr] = useState(null);

  // go / skip / onDone must keep a stable identity across renders: step
  // components pass them straight into a useEffect dependency list, so a fresh
  // function each render would re-fire setNav, re-render, and loop forever.
  const go = useCallback(n => { setNav({}); setSaveErr(null); setI(n); }, [setI]);

  const next = async () => {
    if (nav.onNext) {
      setBusy(true);
      setSaveErr(null);
      let ok = false;
      try {
        ok = await nav.onNext();
      } catch (e) {
        // The step's answers may be saved, but its progress (or Finish) wasn't:
        // stay here and say so, rather than move on as if it had been.
        setSaveErr(e.message || 'Could not save. Try again.');
        ok = false;
      } finally { setBusy(false); }
      if (ok === false) return;
    }
    if (i < steps.length - 1) go(i + 1);
  };

  const stepKey = step.key;
  const skip = useCallback(async () => {
    // Skipping must never trap anyone: if the box didn't record it, the step is
    // simply offered again next time.
    if (stepKey) await mark(stepKey, null, true).catch(() => {});
    setNav({});
    setSaveErr(null);
    setI(n => Math.min(n + 1, steps.length - 1));
  }, [stepKey, mark, steps.length, setI]);

  const onDone = useCallback(detail => mark(stepKey, detail), [mark, stepKey]);

  // Throws when Finish wasn't saved, so the Done screen stays put with the error
  // instead of leaving — the wizard would otherwise open again at the next login.
  const onFinish = useCallback(async () => {
    let r;
    try {
      r = await apiFetch('/ui/setup/state', { method: 'PUT', json: { step: 'finish' } });
    } catch {
      throw new Error('Could not reach the box to finish setup. Check the connection and try again.');
    }
    if (!r.ok) throw new Error('The box did not record that setup is finished. Try again.');
  }, []);

  const stepProps = {
    setNav: setNavStable,
    cameras,
    reloadCameras,
    state,
    onDone,
    onSkip: skip,
    onFinish,
    // Steps shared with the home wizard that ask the same question in different
    // words — the Site step, and the Done screen's summary.
    industrial,
    // Jump straight to a step by id. The last screen uses it to walk the customer
    // back into anything they skipped, which is the half of "you can skip this"
    // that otherwise never happens: a skipped step is not `outstanding`, so nothing
    // would ever mention it again and "come back to it later" would be a promise
    // the product does not keep.
    // steps, not STEPS: navigation indexes the filtered list. With People hidden
    // (no Add to Known) every later step would open one too far (PRVSK-108 #4).
    goToStep: id => { const n = steps.findIndex(s => s.id === id); if (n >= 0) go(n); },
  };

  const Body = {
    welcome:  WelcomeStep,
    property: PropertyStep,
    cameras:  CamerasStep,
    areas:    AreasStep,
    // Same component, fast mode: the two questions that matter, the
    // drawing tool only for cameras that see a road or a neighbour.
    perimeter: PerimeterStep,
    linger:   LingerStep,
    animals:  AnimalsStep,
    models:   ModelsStep,
    modules:  ModulesStep,
    people:   PeopleStep,
    phone:    PhoneStep,
    schedule: ScheduleStep,
    devices:  DevicesStep,
    test:     TestStep,
    ready:    ReadyStep,
  }[step.id];

  const isDone = k => !!(state?.steps?.[k]?.done || state?.steps?.[k]?.skipped);
  const pct = Math.round((i / (steps.length - 1)) * 100);

  return (
    <div className="gs-root">
      <aside className="gs-rail">
        <div className="gs-rail-brand">
          Parvekshak
          <span>Guided setup</span>
        </div>
        <div className="gs-rail-steps">
          {steps.map((s, n) => (
            <button
              key={s.id}
              className={`gs-rail-step${n === i ? ' active' : ''}${s.key && isDone(s.key) ? ' done' : ''}`}
              onClick={() => go(n)}
              disabled={n > i && !(s.key && isDone(s.key))}
            >
              <span className="gs-rail-dot">{s.key && isDone(s.key) && n !== i ? '✓' : n + 1}</span>
              <span>{s.rail}</span>
            </button>
          ))}
        </div>
        {/* The way out lives in the footer, not here. The footer is pinned at every
            screen size, while this rail collapses on a phone and sits below the fold
            on a short desktop window — so a copy here was both redundant and the one
            a customer could not reach when they needed it. */}
      </aside>

      <div className="gs-main">
        <div className="gs-topbar">
          {/* Doubles as the mobile step-jump — the rail that does this on
              desktop is hidden at this width. */}
          <select
            className="gs-topbar-jump"
            value={i}
            onChange={e => go(Number(e.target.value))}
            aria-label="Go to a setup step"
          >
            {steps.map((s, n) => (
              <option key={s.id} value={n} disabled={n > i && !(s.key && isDone(s.key))}>
                {n + 1}. {s.rail}
              </option>
            ))}
          </select>
          <span className="gs-topbar-track"><span className="gs-topbar-fill" style={{ width: `${pct}%` }} /></span>
          <span className="gs-topbar-label">{i + 1}/{steps.length}</span>
        </div>

        <div className="gs-body">
          <div className={`gs-inner${step.id === 'cameras' || step.id === 'areas' ? ' wide' : ''}`}>
            {Body && <Body {...stepProps} />}
            {saveErr && <div className="gs-err" role="alert">{saveErr}</div>}
          </div>
        </div>

        <div className="gs-foot">
          {/* Leaving the wizard sits alone on the LEFT, away from Back/Continue.
              It is in the footer at all because the left rail collapses on a phone,
              which left a customer mid-setup on a small screen with no way out but
              the browser's back button. Kept apart from Back deliberately: side by
              side, a mis-tap aimed at Back drops them out of setup altogether.
              Everything finished so far is saved, so leaving is safe. */}
          <button className="gs-btn ghost gs-foot-exit" onClick={() => navigate('/')} disabled={busy}>
            Finish later
          </button>
          <span className="gs-foot-spacer" />
          {nav.note && <span className="gs-foot-note">{nav.note}</span>}
          {i > 0 && <button className="gs-btn ghost" onClick={() => go(i - 1)} disabled={busy}>Back</button>}
          {nav.skip && (
            <button className="gs-btn" onClick={nav.skip} disabled={busy}>
              {nav.skipLabel || 'Skip'}
            </button>
          )}
          <button className="gs-btn primary" onClick={next} disabled={busy || nav.canNext === false}>
            {busy ? <span className="gs-spin" /> : (nav.nextLabel || 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
